<template>
  <div class="sales-dashboard min-h-screen">
    <LoadingSpinner :isLoading="isLoading" />

    <!-- Header Section -->
    <div class="sd-header bg-gray-50 border-b border-gray-200 shadow-sm">
      <div class="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="sd-header__logo">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-xl font-semibold text-secondary-800">{{ $t('commission_page.title') }}</h1>
              <p class="text-sm text-gray-500">{{ $t('commission_page.subtitle') }}</p>
            </div>
          </div>
          <div class="flex items-center gap-2 flex-wrap justify-end">
            <!-- Year filter -->
            <div v-if="filterYearOptions.length > 0" class="flex items-center space-x-2">
              <label class="text-sm text-gray-600">{{ $t("commission_page.filter_year") }}:</label>
              <select v-model="filterYear" class="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                <option value="">{{ $t("commission_page.all_years") }}</option>
                <option v-for="y in filterYearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
            <!-- Month filter -->
            <div v-if="filterYear" class="flex items-center space-x-2">
              <label class="text-sm text-gray-600">{{ $t("commission_page.filter_month") }}:</label>
              <select v-model="filterMonth" class="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                <option value="">{{ $t("commission_page.all_months") }}</option>
                <option v-for="m in 12" :key="m" :value="m">{{ monthName(m) }}</option>
              </select>
            </div>
            <!-- Seller filter for admin -->
            <div v-if="isAdmin && sellers.length > 1" class="flex items-center space-x-2">
              <label class="text-sm text-gray-600">{{ $t("commission_page.filter_seller") }}:</label>
              <select v-model="selectedSellerId" class="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                <option value="">{{ $t("commission_page.all_sellers") }}</option>
                <option v-for="seller in sellers" :key="seller._id" :value="seller._id">{{ seller.name }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isLoading" class="w-full px-4 sm:px-6 lg:px-8 py-6">
      <!-- Summary Cards (Admin: paid/upcoming split) -->
        <div v-if="isAdmin" class="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6">
          <div class="bg-white rounded-lg shadow p-4">
            <div class="text-sm text-gray-500">{{ $t("commission_page.paid_commission") }}</div>
            <div class="text-2xl font-bold text-green-700 mt-1">{{ formatCurrency(paidCommission) }}</div>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <div class="text-sm text-gray-500">{{ $t("commission_page.upcoming_commission") }}</div>
            <div class="text-2xl font-bold text-amber-700 mt-1">{{ formatCurrency(upcomingCommission) }}</div>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <div class="text-sm text-gray-500">{{ $t("commission_page.total_revenue") }}</div>
            <div class="text-2xl font-bold text-gray-800 mt-1">{{ formatCurrency(totalRevenue) }}</div>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <div class="text-sm text-gray-500">{{ $t("commission_page.avg_margin") }}</div>
            <div class="text-2xl font-bold text-gray-800 mt-1">{{ avgMargin }}%</div>
          </div>
          <div class="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow border border-transparent hover:border-red-200">
            <div class="flex items-center justify-between">
              <div class="cursor-pointer flex-1" @click="showDebtBalanceModal">
                <div class="text-sm text-gray-500">{{ $t("commission_page.outstanding_debt") }}</div>
                <div class="text-2xl font-bold mt-1" :class="totalDebtBalance > 0 ? 'text-red-600' : 'text-gray-400'">{{ formatCurrency(totalDebtBalance) }}</div>
              </div>
              <button
                v-if="totalDebtBalance > 0"
                @click="confirmResetAllDebt"
                class="text-gray-400 hover:text-red-600 transition-colors p-1.5 rounded hover:bg-red-50 ml-2"
                :title="$t('commission_page.reset_all_debt')"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
              </button>
            </div>
          </div>
        </div>
        <!-- Summary Cards (Seller: single total) -->
        <div v-else class="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div class="bg-white rounded-lg shadow p-4">
            <div class="text-sm text-gray-500">{{ $t("commission_page.total_commissions") }}</div>
            <div class="text-2xl font-bold text-gray-800 mt-1">{{ formatCurrency(totalCommission) }}</div>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <div class="text-sm text-gray-500">{{ $t("commission_page.total_revenue") }}</div>
            <div class="text-2xl font-bold text-gray-800 mt-1">{{ formatCurrency(totalRevenue) }}</div>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <div class="text-sm text-gray-500">{{ $t("commission_page.avg_margin") }}</div>
            <div class="text-2xl font-bold text-gray-800 mt-1">{{ avgMargin }}%</div>
          </div>
          <div class="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border border-transparent hover:border-red-200" @click="showDebtBalanceModal">
            <div class="text-sm text-gray-500">{{ $t("commission_page.outstanding_debt") }}</div>
            <div class="text-2xl font-bold mt-1" :class="totalDebtBalance > 0 ? 'text-red-600' : 'text-gray-400'">{{ formatCurrency(totalDebtBalance) }}</div>
          </div>
        </div>

        <!-- Status Tabs -->
        <div class="flex border-b border-gray-200 mb-4 gap-1">
          <button
            @click="commissionTab = 'pending'"
            class="px-4 py-2 text-sm font-medium rounded-t-lg transition-colors"
            :class="commissionTab === 'pending' ? 'bg-white border border-b-white border-gray-200 -mb-px text-primary-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
          >
            {{ $t("commission_page.tab_pending") }}
            <span v-if="pendingCommissions.length" class="ml-1.5 bg-amber-100 text-amber-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">{{ pendingCommissions.length }}</span>
          </button>
          <button
            @click="commissionTab = 'accepted'"
            class="px-4 py-2 text-sm font-medium rounded-t-lg transition-colors"
            :class="commissionTab === 'accepted' ? 'bg-white border border-b-white border-gray-200 -mb-px text-green-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
          >
            {{ $t("commission_page.tab_accepted") }}
            <span v-if="acceptedCommissions.length" class="ml-1.5 bg-green-100 text-green-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">{{ acceptedCommissions.length }}</span>
          </button>
          <button
            @click="commissionTab = 'cancelled'"
            class="px-4 py-2 text-sm font-medium rounded-t-lg transition-colors"
            :class="commissionTab === 'cancelled' ? 'bg-white border border-b-white border-gray-200 -mb-px text-gray-500' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
          >
            {{ $t("commission_page.tab_cancelled") }}
            <span v-if="cancelledCommissions.length" class="ml-1.5 bg-gray-200 text-gray-600 text-xs font-semibold px-1.5 py-0.5 rounded-full">{{ cancelledCommissions.length }}</span>
          </button>
          <button
            @click="commissionTab = 'rejected'"
            class="px-4 py-2 text-sm font-medium rounded-t-lg transition-colors"
            :class="commissionTab === 'rejected' ? 'bg-white border border-b-white border-gray-200 -mb-px text-red-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
          >
            {{ $t("commission_page.tab_rejected") }}
            <span v-if="filteredRejected.length" class="ml-1.5 bg-red-100 text-red-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">{{ filteredRejected.length }}</span>
          </button>
        </div>

        <!-- Top Sellers Board (accepted tab with year+month filter, admin only) -->
        <div v-if="isAdmin && topSellersBoard.length > 0" class="mb-6">
          <div class="rounded-lg p-4" style="background:linear-gradient(to right,#fffbeb,#fefce8);border:1px solid #FCD34D;">
            <div class="flex items-center gap-2 mb-3">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" style="color:#F59E0B;">
                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
              </svg>
              <h3 class="text-sm font-semibold" style="color:#92400E;">{{ filterMonth ? $t('commission_page.top_sellers_title') : $t('commission_page.top_sellers_title_year') }}</h3>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div v-for="(seller, idx) in topSellersBoard" :key="seller.sellerId"
                   class="bg-white rounded-lg p-3 border"
                   :class="idx === 0 ? 'shadow-sm' : ''"
                   :style="idx === 0 ? 'border:1px solid #FCD34D;' : idx === 1 ? 'border:1px solid #D1D5DB;' : 'border:1px solid #FDE68A;'">
                <div class="flex items-center gap-2 mb-2">
                  <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                       :style="idx === 0 ? 'background:#F59E0B;' : idx === 1 ? 'background:#9CA3AF;color:#374151;' : 'background:#92400E;'">
                    {{ idx + 1 }}
                  </div>
                  <span class="text-sm font-semibold text-gray-800 truncate">{{ seller.sellerName }}</span>
                </div>
                <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div>
                    <div class="text-gray-500">{{ $t('commission_page.top_revenue') }}</div>
                    <div class="font-semibold text-gray-800">{{ formatCurrency(seller.totalRevenue) }}</div>
                  </div>
                  <div>
                    <div class="text-gray-500">MRR</div>
                    <div class="font-semibold text-blue-700">{{ formatCurrency(seller.totalMRR) }}</div>
                  </div>
                  <div>
                    <div class="text-gray-500">TB</div>
                    <div class="font-semibold" :class="seller.totalTB >= 0 ? 'text-green-700' : 'text-red-600'">{{ formatCurrency(seller.totalTB) }}</div>
                  </div>
                  <div>
                    <div class="text-gray-500">{{ $t('commission_page.top_commission_payout') }}</div>
                    <div class="font-semibold text-green-700">{{ formatCurrency(seller.totalCommissionPayout) }}</div>
                  </div>
                </div>
                <div class="text-xs text-gray-400 mt-1">{{ seller.dealCount }} {{ $t('commission_page.top_deals') }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- No data message -->
        <div v-if="tabbedCommissions.length === 0" class="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          {{ $t("commission_page.no_commissions") }}
        </div>

        <!-- Commission Table (Pending / Accepted / Cancelled tabs) — Admin grouped view -->
        <div v-else-if="commissionTab !== 'rejected' && isAdmin" class="bg-white rounded-lg shadow">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-white">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-6">
                    <button @click="toggleAllSellers" :title="allSellersExpanded ? $t('commission_page.collapse_all') : $t('commission_page.expand_all')" class="text-gray-400 hover:text-gray-700 transition-colors">
                      <svg :style="{ transform: allSellersExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }" class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                    </button>
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.seller") }}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.contract_period") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.commission") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.revenue") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.cost") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.tb") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.margin") }}</th>
                </tr>
              </thead>
              <tbody v-for="group in groupedTabbedCommissions" :key="group.sellerId" class="bg-white divide-y divide-gray-200">
                <!-- Seller summary row -->
                <tr class="bg-gray-100 hover:bg-gray-200 cursor-pointer select-none" @click="toggleSellerExpand(group.sellerId)">
                  <td class="px-4 py-3 text-sm text-gray-400">
                    <svg :style="{ transform: expandedSellerIds.includes(group.sellerId) ? 'rotate(90deg)' : 'rotate(0deg)' }" class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                  </td>
                  <td class="px-4 py-3 text-sm">
                    <span class="font-semibold text-gray-900">{{ group.sellerName }}</span>
                    <span class="ml-2 text-xs text-gray-400">{{ group.count }} {{ $t("commission_page.commissions_count") }}</span>
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-500">
                    <span v-if="group.avgContractPeriod != null">&#8960; {{ group.avgContractPeriod }} {{ $t("commission_page.months") }}</span>
                    <span v-else class="text-gray-400">—</span>
                  </td>
                  <td class="px-4 py-3 text-sm text-right font-bold text-primary-700">
                    {{ formatCurrency(group.totalCommission) }}
                    <div v-if="group.periodCommission != null" class="text-xs font-semibold text-green-600 mt-0.5">
                      {{ $t("commission_page.period_payout") }}: {{ formatCurrency(group.periodCommission) }}
                    </div>
                  </td>
                  <td class="px-4 py-3 text-sm text-right font-medium text-gray-700">{{ formatCurrency(group.totalRevenue) }}</td>
                  <td class="px-4 py-3 text-sm text-right font-medium text-gray-700">{{ formatCurrency(group.totalCost) }}</td>
                  <td class="px-4 py-3 text-sm text-right font-medium text-gray-700">{{ formatCurrency(group.totalTB) }}</td>
                  <td class="px-4 py-3 text-sm text-right">
                    <span :class="marginClass(group.avgMargin)">{{ group.avgMargin != null ? '&#8960; ' + group.avgMargin + '%' : '—' }}</span>
                  </td>
                </tr>
                <!-- Expanded individual commission rows -->
                <template v-if="expandedSellerIds.includes(group.sellerId)">
                  <tr v-for="row in getGroupedCommissionRows(group.items)" :key="row.key" :class="row.type === 'detail' ? 'bg-gray-50' : (row.item.is_debt_closed ? 'bg-gray-50 opacity-50' : 'bg-white hover:bg-blue-50')" class="cursor-pointer transition-colors" @click="row.type === 'item' ? toggleExpand(row.item.key) : null">
                    <template v-if="row.type === 'item'">
                      <td class="px-4 py-2 text-sm text-gray-300">
                        <svg v-if="expandedKey === row.item.key" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
                      </td>
                      <td class="py-2 text-sm" colspan="1">
                        <div class="pl-2 border-l-2 border-primary-200 flex items-center gap-2 min-w-0">
                          <span class="font-medium flex-shrink-0" :class="row.item.is_debt_closed ? 'text-gray-400 line-through' : 'text-gray-900'">{{ row.item.agreement_number }}</span>
                          <div
                            v-if="isAdmin && hasProductWarning(row.item)"
                            class="relative flex-shrink-0"
                            @mouseenter="showWarningTooltip($event, row.item, 'product')"
                            @mouseleave="hideWarningTooltip"
                          >
                            <svg class="w-3.5 h-3.5 text-red-500 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                          </div>
                          <div
                            v-if="isAdmin && hasOrderMismatch(row.item)"
                            class="relative flex-shrink-0"
                            @mouseenter="showWarningTooltip($event, row.item, 'order')"
                            @mouseleave="hideWarningTooltip"
                          >
                            <svg class="w-3.5 h-3.5 text-red-500 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                          </div>
                          <div
                            v-if="row.item.customer_name"
                            class="relative inline-block min-w-0"
                            @mouseenter="hoveredNameRow = row.item.key"
                            @mouseleave="hoveredNameRow = null"
                          >
                            <span class="block truncate text-gray-500 text-xs" style="max-width: 140px;">{{ row.item.customer_name }}</span>
                            <div
                              v-if="hoveredNameRow === row.item.key"
                              class="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                              style="z-index: 99999;"
                            >
                              {{ row.item.customer_name }}
                              <div class="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td class="px-4 py-2 text-sm text-gray-600">
                        <span v-if="row.item.contract_period">{{ row.item.contract_period }} {{ $t("commission_page.months") }}</span>
                        <span v-else class="text-gray-400">—</span>
                      </td>
                      <td class="px-4 py-2 text-sm text-right font-semibold" :class="row.item.is_debt_closed ? 'text-gray-400' : 'text-primary-600'">{{ formatCurrency(row.item.total_commission) }}</td>
                      <td class="px-4 py-2 text-sm text-right text-gray-600">{{ formatCurrency(row.item.total_revenue) }}</td>
                      <td class="px-4 py-2 text-sm text-right text-gray-600">{{ formatCurrency(row.item.total_cost) }}</td>
                      <td class="px-4 py-2 text-sm text-right text-gray-600">{{ formatCurrency(row.item.adjusted_tb != null ? row.item.adjusted_tb : row.item.total_tb) }}</td>
                      <td class="px-4 py-2 text-sm text-right">
                        <span :class="marginClass(row.item.margin_percent)">{{ row.item.margin_percent != null ? row.item.margin_percent + '%' : '—' }}</span>
                      </td>
                    </template>
                    <template v-else>
                      <td colspan="8" class="px-4 py-4">
                        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-sm mb-3">
                          <div>
                            <span class="text-gray-500">{{ $t("commission_page.contract_start") }}:</span>
                            <span v-if="row.item.contract_start" class="ml-1 text-gray-700">{{ formatDate(row.item.contract_start) }}</span>
                            <span v-else class="ml-1 text-gray-400">—</span>
                          </div>
                          <div>
                            <span class="text-gray-500">{{ $t("commission_page.noted") }}:</span>
                            <span v-if="row.item.is_noted" class="ml-1 text-green-600"><BaseIcon icon="check-circle" class="mr-1" />{{ formatDate(row.item.noted_at) }}</span>
                            <span v-else class="ml-1 text-gray-400">—</span>
                          </div>
                          <div>
                            <span class="text-gray-500">{{ $t("commission_page.utbetalning") }}:</span>
                            <span v-if="getPaymentDisplay(row.item)" class="ml-1 text-gray-700">{{ getPaymentDisplay(row.item) }}</span>
                            <span v-else class="ml-1 text-gray-400">—</span>
                          </div>
                          <div>
                            <span class="text-gray-500">{{ $t("commission_page.status") }}:</span>
                            <span :class="statusBadgeClass(row.item.status)" class="ml-1 inline-flex px-2 py-0.5 text-xs font-medium rounded-full">{{ statusLabel(row.item.status) }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">{{ $t("commission_page.pdf") }}:</span>
                            <a v-if="row.item.pdf_file" :href="row.item.pdf_file" target="_blank" rel="noopener noreferrer" class="ml-1 text-red-600 hover:text-red-800"><BaseIcon icon="file-pdf" /></a>
                            <span v-else class="ml-1 text-gray-400">—</span>
                          </div>
                          <div class="flex items-center gap-1" @click.stop>
                            <span class="text-gray-500">{{ $t("commission_page.actions") }}:</span>
                            <div class="inline-flex items-center border border-gray-200 rounded-md overflow-hidden divide-x divide-gray-200 ml-1">
                              <button @click="openChangeModal(row.item)" class="text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1 transition-colors" :title="$t('commission_page.change')"><BaseIcon icon="edit" /></button>
                              <button @click="acceptCommission(row.item)" class="text-green-600 hover:text-green-800 hover:bg-green-50 px-2 py-1 transition-colors" :title="$t('commission_page.accept')"><BaseIcon icon="check" /></button>
                              <button @click="rejectCommission(row.item)" class="text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 transition-colors" :title="$t('commission_page.reject')"><BaseIcon icon="times" /></button>
                            </div>
                          </div>
                        </div>
                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                          <div v-if="row.item.extra_monthly_cost">
                            <span class="text-gray-500">{{ $t("commission_page.extra_monthly") }}:</span>
                            <span class="ml-1 font-medium">{{ formatCurrency(row.item.extra_monthly_cost) }}</span>
                          </div>
                          <div v-if="row.item.extra_cash_cost">
                            <span class="text-gray-500">{{ $t("commission_page.extra_cash") }}:</span>
                            <span class="ml-1 font-medium">{{ formatCurrency(row.item.extra_cash_cost) }}</span>
                          </div>
                          <div v-if="row.item.total_tb !== row.item.adjusted_tb">
                            <span class="text-gray-500">{{ $t("commission_page.original_tb") }}:</span>
                            <span class="ml-1 font-medium">{{ formatCurrency(row.item.total_tb) }}</span>
                          </div>
                        </div>
                        <!-- Cost breakdown -->
                        <div v-if="row.item.hardware_cost != null || row.item.software_cost != null || row.item.setup_fee_cost != null" class="mt-3 pt-3 border-t border-gray-200">
                          <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.cost_breakdown") }}</div>
                          <div class="flex flex-wrap gap-3">
                            <div v-if="row.item.hardware_cost != null" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"></span>
                              <span class="text-gray-500">{{ $t("commission_page.hardware_cost") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency(row.item.hardware_cost) }}</span>
                            </div>
                            <div v-if="row.item.software_cost != null" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0"></span>
                              <span class="text-gray-500">{{ $t("commission_page.software_cost") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency(row.item.software_cost) }}</span>
                            </div>
                            <div v-if="row.item.setup_fee_cost != null && row.item.setup_fee_cost > 0" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0"></span>
                              <span class="text-gray-500">{{ $t("commission_page.setup_fee_cost") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency(row.item.setup_fee_cost) }}</span>
                            </div>
                            <div v-if="getAddedCosts(row.item) > 0" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="w-2 h-2 rounded-full bg-teal-400 flex-shrink-0"></span>
                              <span class="text-gray-500">{{ $t("commission_page.added_costs") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency(getAddedCosts(row.item)) }}</span>
                            </div>
                            <div v-if="row.item.total_cost != null" class="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="text-gray-500">{{ $t("commission_page.total_cost") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency((row.item.total_cost || 0) + getAddedCosts(row.item)) }}</span>
                            </div>
                          </div>
                        </div>
                        <!-- OMT (Redraw) costs -->
                        <div v-if="row.item.original_agreement_number && row.item.redraw_total_cost" class="mt-3 pt-3 border-t border-gray-200">
                          <div class="p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div class="flex items-center gap-1.5 mb-2">
                              <svg class="w-4 h-4 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                              <span class="text-sm font-semibold text-amber-800">{{ $t('note_commission.redraw_costs_title') }}</span>
                            </div>
                            <p class="text-xs text-amber-700 mb-2">{{ $t('note_commission.redraw_costs_description', { original: row.item.original_agreement_number }) }}</p>
                            <div class="space-y-1">
                              <div v-if="row.item.redraw_remaining_contract_cost" class="flex justify-between items-center text-xs">
                                <span class="text-gray-700">{{ $t('note_commission.redraw_remaining_contract') }}</span>
                                <span class="font-semibold text-red-600">-{{ formatCurrency(row.item.redraw_remaining_contract_cost) }}</span>
                              </div>
                              <div v-if="row.item.redraw_original_commission_paid" class="flex justify-between items-center text-xs">
                                <span class="text-gray-700">{{ $t('note_commission.redraw_original_commission') }}</span>
                                <span class="font-semibold text-red-600">-{{ formatCurrency(row.item.redraw_original_commission_paid) }}</span>
                              </div>
                              <div class="flex justify-between items-center text-xs pt-1 border-t border-amber-200">
                                <span class="font-semibold text-amber-800">{{ $t('note_commission.redraw_total_deduction') }}</span>
                                <span class="font-bold text-red-700">-{{ formatCurrency(row.item.redraw_total_cost) }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- Products breakdown -->
                        <div v-if="row.item.products && row.item.products.length" class="mt-3 pt-3 border-t border-gray-200">
                          <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.products") }}</div>
                          <div class="border border-gray-200 rounded-lg overflow-hidden">
                            <table class="w-full text-xs">
                              <thead class="bg-gray-50">
                                <tr>
                                  <th class="px-3 py-1.5 text-left font-medium text-gray-500">{{ $t("commission_page.product_name") }}</th>
                                  <th class="px-3 py-1.5 text-left font-medium text-gray-500">{{ $t("commission_page.type") }}</th>
                                  <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.qty") }}</th>
                                  <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.sale_price") }}</th>
                                  <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.purchase_price") }}</th>
                                </tr>
                              </thead>
                              <tbody class="divide-y divide-gray-100">
                                <tr v-for="(prod, pIdx) in row.item.products" :key="pIdx" class="hover:bg-gray-50">
                                  <td class="px-3 py-1.5 text-gray-800">{{ prod.name }}</td>
                                  <td class="px-3 py-1.5 text-gray-500">{{ prod.is_cash ? $t('commission_page.cash') : $t('commission_page.rental') }}</td>
                                  <td class="px-3 py-1.5 text-right text-gray-700">{{ prod.qty }}</td>
                                  <td class="px-3 py-1.5 text-right text-gray-700">{{ formatCurrency(prod.sale_price) }}</td>
                                  <td class="px-3 py-1.5 text-right text-gray-700">{{ formatCurrency(prod.purchase_price) }}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <!-- Services: Alarm center & SIM card -->
                          <div v-if="(row.item.alarm_center && row.item.alarm_center.qty > 0) || (row.item.simcard && row.item.simcard.qty > 0)" class="mt-2">
                            <div class="text-xs font-medium text-gray-500 uppercase mb-1.5">{{ $t("commission_page.services") }}</div>
                            <div class="flex flex-wrap gap-3">
                              <div v-if="row.item.alarm_center && row.item.alarm_center.qty > 0" class="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-xs">
                                <span class="text-blue-600 font-medium">{{ $t("commission_page.alarm_center") }}:</span>
                                <span class="text-gray-800">{{ row.item.alarm_center.name }}</span>
                                <span class="text-gray-500">{{ row.item.alarm_center.qty }} × {{ formatCurrency(row.item.alarm_center.cost_per_month) }}{{ $t("commission_page.per_month") }}</span>
                              </div>
                              <div v-if="row.item.simcard && row.item.simcard.qty > 0" class="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded px-3 py-1.5 text-xs">
                                <span class="text-green-600 font-medium">{{ $t("commission_page.simcard") }}:</span>
                                <span class="text-gray-800">{{ row.item.simcard.name }}</span>
                                <span class="text-gray-500">{{ row.item.simcard.qty }} × {{ formatCurrency(row.item.simcard.cost_per_month) }}{{ $t("commission_page.per_month") }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else class="mt-3 pt-3 border-t border-gray-200">
                          <button
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            :disabled="fetchingProductsKey === row.item.key"
                            @click="fetchProductsForCommission(row.item)"
                          >
                            <svg v-if="fetchingProductsKey === row.item.key" class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                            <svg v-else class="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                            {{ $t("commission_page.fetch_products") }}
                          </button>
                        </div>
                        <!-- Yearly breakdown -->
                        <div v-if="hasYearlyBreakdown(row.item)" class="mt-3">
                          <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.yearly_breakdown") }}</div>
                          <div class="flex flex-wrap gap-2">
                            <div v-for="year in getYearlyBreakdown(row.item)" :key="year.label" class="bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="text-gray-500">{{ year.label }}:</span>
                              <span class="ml-1 font-semibold text-primary-700">{{ formatCurrency(year.value) }}</span>
                              <span v-if="year.paymentDate" class="ml-1.5 text-[10px] text-gray-400">({{ year.paymentDate }})</span>
                              <span v-if="year.isPaid" class="ml-1 text-[10px] text-green-600 font-medium">&#10003; {{ $t("commission_page.paid_badge") }}</span>
                            </div>
                          </div>
                        </div>
                        <!-- Status history -->
                        <div v-if="row.item.status_changed_at" class="mt-3 text-xs text-gray-400">
                          {{ $t("commission_page.last_updated") }}: {{ formatDateTime(row.item.status_changed_at) }}
                          <span v-if="row.item.status_changed_by"> — {{ row.item.status_changed_by }}</span>
                        </div>
                      </td>
                    </template>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Commission Table (Pending / Accepted / Cancelled tabs) — Non-admin flat view -->
        <div v-else-if="commissionTab !== 'rejected' && !isAdmin" class="bg-white rounded-lg shadow">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.agreement") }}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.customer") }}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.contract_period") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.commission") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.revenue") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.cost") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.tb") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.margin") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.noted") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.utbetalning") }}</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.status") }}</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.pdf") }}</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.actions") }}</th>
                </tr>
              </thead>
              <tbody v-for="item in tabbedCommissions" :key="item.key" class="bg-white divide-y divide-gray-200">
                  <tr :class="item.is_debt_closed ? 'bg-gray-50 opacity-50' : 'bg-white hover:bg-blue-50'" class="cursor-pointer transition-colors" @click="toggleExpand(item.key)">
                    <td class="px-4 py-3 text-sm font-medium" :class="item.is_debt_closed ? 'text-gray-400 line-through' : 'text-gray-900'">
                      <div class="flex items-center gap-1.5">
                        <span>{{ item.agreement_number }}</span>
                        <div
                          v-if="isAdmin && hasProductWarning(item)"
                          class="relative flex-shrink-0"
                          @mouseenter="showWarningTooltip($event, item, 'product')"
                          @mouseleave="hideWarningTooltip"
                          @click.stop
                        >
                          <svg class="w-3.5 h-3.5 text-red-500 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                        </div>
                        <div
                          v-if="isAdmin && hasOrderMismatch(item)"
                          class="relative flex-shrink-0"
                          @mouseenter="showWarningTooltip($event, item, 'order')"
                          @mouseleave="hideWarningTooltip"
                          @click.stop
                        >
                          <svg class="w-3.5 h-3.5 text-red-500 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-700">
                      <div
                        v-if="item.customer_name"
                        class="relative inline-block"
                        @mouseenter="hoveredNameRow = item.key"
                        @mouseleave="hoveredNameRow = null"
                      >
                        <span class="block truncate" style="max-width: 140px;">{{ item.customer_name }}</span>
                        <div
                          v-if="hoveredNameRow === item.key"
                          class="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                          style="z-index: 99999;"
                        >
                          {{ item.customer_name }}
                          <div class="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-700">
                      <span v-if="item.contract_period">{{ item.contract_period }} {{ $t("commission_page.months") }}</span>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                    <td class="px-4 py-3 text-sm text-right font-semibold" :class="item.is_debt_closed ? 'text-gray-400' : 'text-primary-700'">{{ formatCurrency(item.total_commission) }}</td>
                    <td class="px-4 py-3 text-sm text-right text-gray-700">{{ formatCurrency(item.total_revenue) }}</td>
                    <td class="px-4 py-3 text-sm text-right text-gray-700">{{ formatCurrency(item.total_cost) }}</td>
                    <td class="px-4 py-3 text-sm text-right text-gray-700">{{ formatCurrency(item.adjusted_tb != null ? item.adjusted_tb : item.total_tb) }}</td>
                    <td class="px-4 py-3 text-sm text-right">
                      <span :class="marginClass(item.margin_percent)">{{ item.margin_percent != null ? item.margin_percent + '%' : '—' }}</span>
                    </td>
                    <td class="px-4 py-3 text-sm text-right text-gray-500">
                      <span v-if="item.is_noted" class="text-green-600">
                        <BaseIcon icon="check-circle" class="mr-1" />{{ formatDate(item.noted_at) }}
                      </span>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                    <td class="px-4 py-3 text-sm text-right">
                      <span v-if="getPaymentDisplay(item)" class="text-gray-700">{{ getPaymentDisplay(item) }}</span>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                    <td class="px-4 py-3 text-center">
                      <span :class="statusBadgeClass(item.status)" class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full">{{ statusLabel(item.status) }}</span>
                    </td>
                    <td class="px-4 py-3 text-center" @click.stop>
                      <a v-if="item.pdf_file" :href="item.pdf_file" target="_blank" rel="noopener noreferrer" class="inline-flex items-center text-red-600 hover:text-red-800 transition-colors" :title="$t('commission_page.view_pdf')"><BaseIcon icon="file-pdf" /></a>
                      <span v-else class="text-gray-300">—</span>
                    </td>
                    <td class="px-4 py-3 text-center" @click.stop>
                      <button
                        v-if="item.status !== 'accepted'"
                        @click="openChangeModal(item)"
                        class="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                        :title="$t('commission_page.edit')"
                      ><BaseIcon icon="edit" /></button>
                    </td>
                  </tr>
                  <!-- Expanded detail row -->
                  <tr v-if="expandedKey === item.key" class="bg-gray-50">
                    <td colspan="13" class="px-4 py-4">
                      <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span class="text-gray-500">{{ $t("commission_page.contract_start") }}:</span>
                          <span v-if="item.contract_start" class="ml-1 text-gray-700">{{ formatDate(item.contract_start) }}</span>
                          <span v-else class="ml-1 text-gray-400">—</span>
                        </div>
                        <div v-if="item.extra_monthly_cost">
                          <span class="text-gray-500">{{ $t("commission_page.extra_monthly") }}:</span>
                          <span class="ml-1 font-medium">{{ formatCurrency(item.extra_monthly_cost) }}</span>
                        </div>
                        <div v-if="item.extra_cash_cost">
                          <span class="text-gray-500">{{ $t("commission_page.extra_cash") }}:</span>
                          <span class="ml-1 font-medium">{{ formatCurrency(item.extra_cash_cost) }}</span>
                        </div>
                        <div v-if="item.total_tb !== item.adjusted_tb">
                          <span class="text-gray-500">{{ $t("commission_page.original_tb") }}:</span>
                          <span class="ml-1 font-medium">{{ formatCurrency(item.total_tb) }}</span>
                        </div>
                      </div>
                      <div v-if="item.hardware_cost != null || item.software_cost != null || item.setup_fee_cost != null" class="mt-3 pt-3 border-t border-gray-200">
                        <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.cost_breakdown") }}</div>
                        <div class="flex flex-wrap gap-3">
                          <div v-if="item.hardware_cost != null" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"></span>
                            <span class="text-gray-500">{{ $t("commission_page.hardware_cost") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency(item.hardware_cost) }}</span>
                          </div>
                          <div v-if="item.software_cost != null" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0"></span>
                            <span class="text-gray-500">{{ $t("commission_page.software_cost") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency(item.software_cost) }}</span>
                          </div>
                          <div v-if="item.setup_fee_cost != null && item.setup_fee_cost > 0" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0"></span>
                            <span class="text-gray-500">{{ $t("commission_page.setup_fee_cost") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency(item.setup_fee_cost) }}</span>
                          </div>
                          <div v-if="getAddedCosts(item) > 0" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="w-2 h-2 rounded-full bg-teal-400 flex-shrink-0"></span>
                            <span class="text-gray-500">{{ $t("commission_page.added_costs") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency(getAddedCosts(item)) }}</span>
                          </div>
                          <div v-if="item.total_cost != null" class="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="text-gray-500">{{ $t("commission_page.total_cost") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency((item.total_cost || 0) + getAddedCosts(item)) }}</span>
                          </div>
                        </div>
                      </div>
                      <!-- OMT (Redraw) costs -->
                      <div v-if="item.original_agreement_number && item.redraw_total_cost" class="mt-3 pt-3 border-t border-gray-200">
                        <div class="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <div class="flex items-center gap-1.5 mb-2">
                            <svg class="w-4 h-4 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                            <span class="text-sm font-semibold text-amber-800">{{ $t('note_commission.redraw_costs_title') }}</span>
                          </div>
                          <p class="text-xs text-amber-700 mb-2">{{ $t('note_commission.redraw_costs_description', { original: item.original_agreement_number }) }}</p>
                          <div class="space-y-1">
                            <div v-if="item.redraw_remaining_contract_cost" class="flex justify-between items-center text-xs">
                              <span class="text-gray-700">{{ $t('note_commission.redraw_remaining_contract') }}</span>
                              <span class="font-semibold text-red-600">-{{ formatCurrency(item.redraw_remaining_contract_cost) }}</span>
                            </div>
                            <div v-if="item.redraw_original_commission_paid" class="flex justify-between items-center text-xs">
                              <span class="text-gray-700">{{ $t('note_commission.redraw_original_commission') }}</span>
                              <span class="font-semibold text-red-600">-{{ formatCurrency(item.redraw_original_commission_paid) }}</span>
                            </div>
                            <div class="flex justify-between items-center text-xs pt-1 border-t border-amber-200">
                              <span class="font-semibold text-amber-800">{{ $t('note_commission.redraw_total_deduction') }}</span>
                              <span class="font-bold text-red-700">-{{ formatCurrency(item.redraw_total_cost) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Products breakdown -->
                      <div v-if="item.products && item.products.length" class="mt-3 pt-3 border-t border-gray-200">
                        <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.products") }}</div>
                        <div class="border border-gray-200 rounded-lg overflow-hidden">
                          <table class="w-full text-xs">
                            <thead class="bg-gray-50">
                              <tr>
                                <th class="px-3 py-1.5 text-left font-medium text-gray-500">{{ $t("commission_page.product_name") }}</th>
                                <th class="px-3 py-1.5 text-left font-medium text-gray-500">{{ $t("commission_page.type") }}</th>
                                <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.qty") }}</th>
                                <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.sale_price") }}</th>
                                <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.purchase_price") }}</th>
                              </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                              <tr v-for="(prod, pIdx) in item.products" :key="pIdx" class="hover:bg-gray-50">
                                <td class="px-3 py-1.5 text-gray-800">{{ prod.name }}</td>
                                <td class="px-3 py-1.5 text-gray-500">{{ prod.is_cash ? $t('commission_page.cash') : $t('commission_page.rental') }}</td>
                                <td class="px-3 py-1.5 text-right text-gray-700">{{ prod.qty }}</td>
                                <td class="px-3 py-1.5 text-right text-gray-700">{{ formatCurrency(prod.sale_price) }}</td>
                                <td class="px-3 py-1.5 text-right text-gray-700">{{ formatCurrency(prod.purchase_price) }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <!-- Services: Alarm center & SIM card -->
                        <div v-if="(item.alarm_center && item.alarm_center.qty > 0) || (item.simcard && item.simcard.qty > 0)" class="mt-2">
                          <div class="text-xs font-medium text-gray-500 uppercase mb-1.5">{{ $t("commission_page.services") }}</div>
                          <div class="flex flex-wrap gap-3">
                            <div v-if="item.alarm_center && item.alarm_center.qty > 0" class="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-xs">
                              <span class="text-blue-600 font-medium">{{ $t("commission_page.alarm_center") }}:</span>
                              <span class="text-gray-800">{{ item.alarm_center.name }}</span>
                              <span class="text-gray-500">{{ item.alarm_center.qty }} × {{ formatCurrency(item.alarm_center.cost_per_month) }}{{ $t("commission_page.per_month") }}</span>
                            </div>
                            <div v-if="item.simcard && item.simcard.qty > 0" class="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded px-3 py-1.5 text-xs">
                              <span class="text-green-600 font-medium">{{ $t("commission_page.simcard") }}:</span>
                              <span class="text-gray-800">{{ item.simcard.name }}</span>
                              <span class="text-gray-500">{{ item.simcard.qty }} × {{ formatCurrency(item.simcard.cost_per_month) }}{{ $t("commission_page.per_month") }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="mt-3 pt-3 border-t border-gray-200">
                        <button
                          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          :disabled="fetchingProductsKey === item.key"
                          @click="fetchProductsForCommission(item)"
                        >
                          <svg v-if="fetchingProductsKey === item.key" class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                          <svg v-else class="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                          {{ $t("commission_page.fetch_products") }}
                        </button>
                      </div>
                      <div v-if="hasYearlyBreakdown(item)" class="mt-3">
                        <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.yearly_breakdown") }}</div>
                        <div class="flex flex-wrap gap-2">
                          <div v-for="year in getYearlyBreakdown(item)" :key="year.label" class="bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="text-gray-500">{{ year.label }}:</span>
                            <span class="ml-1 font-semibold text-primary-700">{{ formatCurrency(year.value) }}</span>
                            <span v-if="year.paymentDate" class="ml-1.5 text-[10px] text-gray-400">({{ year.paymentDate }})</span>
                            <span v-if="year.isPaid" class="ml-1 text-[10px] text-green-600 font-medium">✓ {{ $t("commission_page.paid_badge") }}</span>
                          </div>
                        </div>
                      </div>
                      <div v-if="item.status_changed_at" class="mt-3 text-xs text-gray-400">
                        {{ $t("commission_page.last_updated") }}: {{ formatDateTime(item.status_changed_at) }}
                        <span v-if="item.status_changed_by"> — {{ item.status_changed_by }}</span>
                      </div>
                    </td>
                  </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Rejected Commissions — Admin grouped view -->
        <div v-else-if="commissionTab === 'rejected' && isAdmin" class="bg-white rounded-lg shadow border border-red-100">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-red-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-6">
                    <button @click="toggleAllSellers" :title="allSellersExpanded ? $t('commission_page.collapse_all') : $t('commission_page.expand_all')" class="text-gray-400 hover:text-gray-700 transition-colors">
                      <svg :style="{ transform: allSellersExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }" class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                    </button>
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.seller") }}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.contract_period") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.commission") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.revenue") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.tb") }}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.margin") }}</th>
                </tr>
              </thead>
              <tbody v-for="group in groupedRejected" :key="group.sellerId" class="bg-white divide-y divide-gray-200">
                <tr class="bg-red-50 hover:bg-red-100 cursor-pointer select-none opacity-80" @click="toggleSellerExpand(group.sellerId)">
                  <td class="px-4 py-3 text-sm text-gray-400">
                    <svg :style="{ transform: expandedSellerIds.includes(group.sellerId) ? 'rotate(90deg)' : 'rotate(0deg)' }" class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                  </td>
                  <td class="px-4 py-3 text-sm">
                    <span class="font-semibold text-gray-700">{{ group.sellerName }}</span>
                    <span class="ml-2 text-xs text-gray-400">{{ group.count }} {{ $t("commission_page.commissions_count") }}</span>
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-500">
                    <span v-if="group.avgContractPeriod != null">&#8960; {{ group.avgContractPeriod }} {{ $t("commission_page.months") }}</span>
                    <span v-else class="text-gray-400">—</span>
                  </td>
                  <td class="px-4 py-3 text-sm text-right font-bold text-gray-500">{{ formatCurrency(group.totalCommission) }}</td>
                  <td class="px-4 py-3 text-sm text-right font-medium text-gray-500">{{ formatCurrency(group.totalRevenue) }}</td>
                  <td class="px-4 py-3 text-sm text-right font-medium text-gray-500">{{ formatCurrency(group.totalTB) }}</td>
                  <td class="px-4 py-3 text-sm text-right text-gray-500">{{ group.avgMargin != null ? '&#8960; ' + group.avgMargin + '%' : '—' }}</td>
                </tr>
                <template v-if="expandedSellerIds.includes(group.sellerId)">
                  <tr v-for="row in getGroupedCommissionRows(group.items)" :key="row.key" :class="row.type === 'detail' ? 'bg-red-50/30' : 'opacity-80 bg-white hover:bg-red-50'" class="cursor-pointer transition-colors" @click="row.type === 'item' ? toggleExpand(row.item.key) : null">
                    <template v-if="row.type === 'item'">
                      <td class="px-4 py-2 text-sm text-gray-300">
                        <svg v-if="expandedKey === row.item.key" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
                      </td>
                      <td class="py-2 text-sm" colspan="1">
                        <div class="pl-2 border-l-2 border-red-200 flex items-center gap-2 min-w-0">
                          <span class="font-medium text-gray-500 line-through flex-shrink-0">{{ row.item.agreement_number }}</span>
                          <div
                            v-if="isAdmin && hasProductWarning(row.item)"
                            class="relative flex-shrink-0"
                            @mouseenter="showWarningTooltip($event, row.item, 'product')"
                            @mouseleave="hideWarningTooltip"
                          >
                            <svg class="w-3.5 h-3.5 text-red-500 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                          </div>
                          <div
                            v-if="isAdmin && hasOrderMismatch(row.item)"
                            class="relative flex-shrink-0"
                            @mouseenter="showWarningTooltip($event, row.item, 'order')"
                            @mouseleave="hideWarningTooltip"
                          >
                            <svg class="w-3.5 h-3.5 text-red-500 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                          </div>
                          <div
                            v-if="row.item.customer_name"
                            class="relative inline-block min-w-0"
                            @mouseenter="hoveredNameRow = row.item.key"
                            @mouseleave="hoveredNameRow = null"
                          >
                            <span class="block truncate text-gray-400 text-xs" style="max-width: 140px;">{{ row.item.customer_name }}</span>
                            <div
                              v-if="hoveredNameRow === row.item.key"
                              class="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                              style="z-index: 99999;"
                            >
                              {{ row.item.customer_name }}
                              <div class="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td class="px-4 py-2 text-sm text-gray-400">
                        <span v-if="row.item.contract_period">{{ row.item.contract_period }} {{ $t("commission_page.months") }}</span>
                        <span v-else>—</span>
                      </td>
                      <td class="px-4 py-2 text-sm text-right text-gray-500">{{ formatCurrency(row.item.total_commission) }}</td>
                      <td class="px-4 py-2 text-sm text-right text-gray-500">{{ formatCurrency(row.item.total_revenue) }}</td>
                      <td class="px-4 py-2 text-sm text-right text-gray-500">{{ formatCurrency(row.item.adjusted_tb != null ? row.item.adjusted_tb : row.item.total_tb) }}</td>
                      <td class="px-4 py-2 text-sm text-right text-gray-500">{{ row.item.margin_percent != null ? row.item.margin_percent + '%' : '—' }}</td>
                    </template>
                    <template v-else>
                      <td colspan="7" class="px-4 py-4">
                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mb-3">
                          <div>
                            <span class="text-gray-500">{{ $t("commission_page.contract_start") }}:</span>
                            <span class="ml-1 text-gray-500">{{ row.item.contract_start ? formatDate(row.item.contract_start) : '—' }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">{{ $t("commission_page.noted") }}:</span>
                            <span class="ml-1 text-gray-500">{{ row.item.noted_at ? formatDate(row.item.noted_at) : '—' }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">{{ $t("commission_page.last_updated") }}:</span>
                            <span class="ml-1 text-gray-500">{{ row.item.status_changed_at ? formatDate(row.item.status_changed_at) : '—' }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">{{ $t("commission_page.status_changed_by") }}:</span>
                            <span class="ml-1 text-gray-500">{{ row.item.status_changed_by || '—' }}</span>
                          </div>
                        </div>
                        <div v-if="row.item.comment" class="mb-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                          <BaseIcon icon="times" class="text-red-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <span class="font-medium text-red-700">{{ $t("commission_page.reject_comment") }}:</span>
                            <span class="ml-1 text-red-600">{{ row.item.comment }}</span>
                          </div>
                        </div>
                        <div v-if="row.item.hardware_cost != null || row.item.software_cost != null || row.item.setup_fee_cost != null" class="mt-1 pt-2 border-t border-red-100">
                          <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.cost_breakdown") }}</div>
                          <div class="flex flex-wrap gap-3">
                            <div v-if="row.item.hardware_cost != null" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"></span>
                              <span class="text-gray-500">{{ $t("commission_page.hardware_cost") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency(row.item.hardware_cost) }}</span>
                            </div>
                            <div v-if="row.item.software_cost != null" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0"></span>
                              <span class="text-gray-500">{{ $t("commission_page.software_cost") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency(row.item.software_cost) }}</span>
                            </div>
                            <div v-if="row.item.setup_fee_cost != null && row.item.setup_fee_cost > 0" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0"></span>
                              <span class="text-gray-500">{{ $t("commission_page.setup_fee_cost") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency(row.item.setup_fee_cost) }}</span>
                            </div>
                            <div v-if="getAddedCosts(row.item) > 0" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="w-2 h-2 rounded-full bg-teal-400 flex-shrink-0"></span>
                              <span class="text-gray-500">{{ $t("commission_page.added_costs") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency(getAddedCosts(row.item)) }}</span>
                            </div>
                            <div v-if="row.item.total_cost != null" class="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="text-gray-500">{{ $t("commission_page.total_cost") }}:</span>
                              <span class="font-semibold text-gray-800">{{ formatCurrency((row.item.total_cost || 0) + getAddedCosts(row.item)) }}</span>
                            </div>
                          </div>
                        </div>
                        <!-- OMT (Redraw) costs -->
                        <div v-if="row.item.original_agreement_number && row.item.redraw_total_cost" class="mt-3 pt-3 border-t border-gray-200">
                          <div class="p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div class="flex items-center gap-1.5 mb-2">
                              <svg class="w-4 h-4 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                              <span class="text-sm font-semibold text-amber-800">{{ $t('note_commission.redraw_costs_title') }}</span>
                            </div>
                            <p class="text-xs text-amber-700 mb-2">{{ $t('note_commission.redraw_costs_description', { original: row.item.original_agreement_number }) }}</p>
                            <div class="space-y-1">
                              <div v-if="row.item.redraw_remaining_contract_cost" class="flex justify-between items-center text-xs">
                                <span class="text-gray-700">{{ $t('note_commission.redraw_remaining_contract') }}</span>
                                <span class="font-semibold text-red-600">-{{ formatCurrency(row.item.redraw_remaining_contract_cost) }}</span>
                              </div>
                              <div v-if="row.item.redraw_original_commission_paid" class="flex justify-between items-center text-xs">
                                <span class="text-gray-700">{{ $t('note_commission.redraw_original_commission') }}</span>
                                <span class="font-semibold text-red-600">-{{ formatCurrency(row.item.redraw_original_commission_paid) }}</span>
                              </div>
                              <div class="flex justify-between items-center text-xs pt-1 border-t border-amber-200">
                                <span class="font-semibold text-amber-800">{{ $t('note_commission.redraw_total_deduction') }}</span>
                                <span class="font-bold text-red-700">-{{ formatCurrency(row.item.redraw_total_cost) }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- Products breakdown (rejected admin) -->
                        <div v-if="row.item.products && row.item.products.length" class="mt-3 pt-3 border-t border-red-100">
                          <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.products") }}</div>
                          <div class="border border-gray-200 rounded-lg overflow-hidden">
                            <table class="w-full text-xs">
                              <thead class="bg-gray-50">
                                <tr>
                                  <th class="px-3 py-1.5 text-left font-medium text-gray-500">{{ $t("commission_page.product_name") }}</th>
                                  <th class="px-3 py-1.5 text-left font-medium text-gray-500">{{ $t("commission_page.type") }}</th>
                                  <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.qty") }}</th>
                                  <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.sale_price") }}</th>
                                  <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.purchase_price") }}</th>
                                </tr>
                              </thead>
                              <tbody class="divide-y divide-gray-100">
                                <tr v-for="(prod, pIdx) in row.item.products" :key="pIdx" class="hover:bg-gray-50">
                                  <td class="px-3 py-1.5 text-gray-800">{{ prod.name }}</td>
                                  <td class="px-3 py-1.5 text-gray-500">{{ prod.is_cash ? $t('commission_page.cash') : $t('commission_page.rental') }}</td>
                                  <td class="px-3 py-1.5 text-right text-gray-700">{{ prod.qty }}</td>
                                  <td class="px-3 py-1.5 text-right text-gray-700">{{ formatCurrency(prod.sale_price) }}</td>
                                  <td class="px-3 py-1.5 text-right text-gray-700">{{ formatCurrency(prod.purchase_price) }}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <div v-if="(row.item.alarm_center && row.item.alarm_center.qty > 0) || (row.item.simcard && row.item.simcard.qty > 0)" class="mt-2">
                            <div class="text-xs font-medium text-gray-500 uppercase mb-1.5">{{ $t("commission_page.services") }}</div>
                            <div class="flex flex-wrap gap-3">
                              <div v-if="row.item.alarm_center && row.item.alarm_center.qty > 0" class="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-xs">
                                <span class="text-blue-600 font-medium">{{ $t("commission_page.alarm_center") }}:</span>
                                <span class="text-gray-800">{{ row.item.alarm_center.name }}</span>
                                <span class="text-gray-500">{{ row.item.alarm_center.qty }} × {{ formatCurrency(row.item.alarm_center.cost_per_month) }}{{ $t("commission_page.per_month") }}</span>
                              </div>
                              <div v-if="row.item.simcard && row.item.simcard.qty > 0" class="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded px-3 py-1.5 text-xs">
                                <span class="text-green-600 font-medium">{{ $t("commission_page.simcard") }}:</span>
                                <span class="text-gray-800">{{ row.item.simcard.name }}</span>
                                <span class="text-gray-500">{{ row.item.simcard.qty }} × {{ formatCurrency(row.item.simcard.cost_per_month) }}{{ $t("commission_page.per_month") }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else class="mt-3 pt-3 border-t border-red-100">
                          <button
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            :disabled="fetchingProductsKey === row.item.key"
                            @click="fetchProductsForCommission(row.item)"
                          >
                            <svg v-if="fetchingProductsKey === row.item.key" class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                            <svg v-else class="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                            {{ $t("commission_page.fetch_products") }}
                          </button>
                        </div>
                        <div v-if="hasYearlyBreakdown(row.item)" class="mt-3">
                          <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.yearly_breakdown") }}</div>
                          <div class="flex flex-wrap gap-2">
                            <div v-for="year in getYearlyBreakdown(row.item)" :key="year.label" class="bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                              <span class="text-gray-500">{{ year.label }}:</span>
                              <span class="ml-1 font-semibold text-gray-600">{{ formatCurrency(year.value) }}</span>
                            </div>
                          </div>
                        </div>
                        <div v-if="row.item.status_changed_at" class="mt-3 text-xs text-gray-400">
                          {{ $t("commission_page.last_updated") }}: {{ formatDateTime(row.item.status_changed_at) }}
                          <span v-if="row.item.status_changed_by"> — {{ row.item.status_changed_by }}</span>
                        </div>
                        <div class="mt-3 flex gap-2" @click.stop>
                          <button
                            @click="openChangeModal(row.item)"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                            :title="$t('commission_page.edit')"
                          ><BaseIcon icon="edit" class="h-3.5 w-3.5" /> {{ $t("commission_page.edit") }}</button>
                          <button
                            @click="removeCommission(row.item)"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                            :title="$t('commission_page.remove_commission')"
                          ><BaseIcon icon="trash" class="h-3.5 w-3.5" /> {{ $t("commission_page.remove_commission") }}</button>
                        </div>
                      </td>
                    </template>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Rejected Commissions — Non-admin flat view -->
        <div v-else-if="commissionTab === 'rejected' && !isAdmin" class="bg-white rounded-lg shadow border border-red-100">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-red-50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.agreement") }}</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.customer") }}</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.contract_period") }}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.commission") }}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.revenue") }}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.tb") }}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.margin") }}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.noted") }}</th>
                    <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.last_updated") }}</th>
                    <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t("commission_page.actions") }}</th>
                  </tr>
                </thead>
                <tbody v-for="item in filteredRejected" :key="item.key" class="bg-white divide-y divide-gray-200">
                  <tr class="opacity-80 cursor-pointer bg-white hover:bg-red-50 transition-colors" @click="toggleExpand(item.key)">
                    <td class="px-4 py-3 text-sm font-medium text-gray-500 line-through">
                      <div class="flex items-center gap-1.5">
                        <span>{{ item.agreement_number }}</span>
                        <div
                          v-if="isAdmin && hasProductWarning(item)"
                          class="relative flex-shrink-0"
                          @mouseenter="showWarningTooltip($event, item, 'product')"
                          @mouseleave="hideWarningTooltip"
                          @click.stop
                        >
                          <svg class="w-3.5 h-3.5 text-red-500 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                        </div>
                        <div
                          v-if="isAdmin && hasOrderMismatch(item)"
                          class="relative flex-shrink-0"
                          @mouseenter="showWarningTooltip($event, item, 'order')"
                          @mouseleave="hideWarningTooltip"
                          @click.stop
                        >
                          <svg class="w-3.5 h-3.5 text-red-500 cursor-help" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-400">
                      <div
                        v-if="item.customer_name"
                        class="relative inline-block"
                        @mouseenter="hoveredNameRow = item.key"
                        @mouseleave="hoveredNameRow = null"
                      >
                        <span class="block truncate" style="max-width: 140px;">{{ item.customer_name }}</span>
                        <div
                          v-if="hoveredNameRow === item.key"
                          class="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                          style="z-index: 99999;"
                        >
                          {{ item.customer_name }}
                          <div class="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-400">
                      <span v-if="item.contract_period">{{ item.contract_period }} {{ $t("commission_page.months") }}</span>
                      <span v-else>—</span>
                    </td>
                    <td class="px-4 py-3 text-sm text-right text-gray-500">{{ formatCurrency(item.total_commission) }}</td>
                    <td class="px-4 py-3 text-sm text-right text-gray-500">{{ formatCurrency(item.total_revenue) }}</td>
                    <td class="px-4 py-3 text-sm text-right text-gray-500">{{ formatCurrency(item.adjusted_tb != null ? item.adjusted_tb : item.total_tb) }}</td>
                    <td class="px-4 py-3 text-sm text-right text-gray-500">{{ item.margin_percent != null ? item.margin_percent + '%' : '—' }}</td>
                    <td class="px-4 py-3 text-sm text-right text-gray-500">{{ item.noted_at ? formatDate(item.noted_at) : '—' }}</td>
                    <td class="px-4 py-3 text-sm text-center text-gray-500">{{ item.status_changed_at ? formatDate(item.status_changed_at) : '—' }}</td>
                    <td class="px-4 py-3 text-center" @click.stop>
                      <button
                        @click="openChangeModal(item)"
                        class="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                        :title="$t('commission_page.edit')"
                      ><BaseIcon icon="edit" /></button>
                    </td>
                  </tr>
                  <tr v-if="expandedKey === item.key" class="bg-red-50/30">
                    <td colspan="10" class="px-4 py-4">
                      <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mb-3">
                        <div>
                          <span class="text-gray-500">{{ $t("commission_page.contract_start") }}:</span>
                          <span class="ml-1 text-gray-500">{{ item.contract_start ? formatDate(item.contract_start) : '—' }}</span>
                        </div>
                      </div>
                      <div v-if="item.comment" class="mb-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                        <BaseIcon icon="times" class="text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span class="font-medium text-red-700">{{ $t("commission_page.reject_comment") }}:</span>
                          <span class="ml-1 text-red-600">{{ item.comment }}</span>
                        </div>
                      </div>
                      <div v-if="item.hardware_cost != null || item.software_cost != null || item.setup_fee_cost != null" class="mt-1 pt-2 border-t border-red-100">
                        <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.cost_breakdown") }}</div>
                        <div class="flex flex-wrap gap-3">
                          <div v-if="item.hardware_cost != null" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"></span>
                            <span class="text-gray-500">{{ $t("commission_page.hardware_cost") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency(item.hardware_cost) }}</span>
                          </div>
                          <div v-if="item.software_cost != null" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0"></span>
                            <span class="text-gray-500">{{ $t("commission_page.software_cost") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency(item.software_cost) }}</span>
                          </div>
                          <div v-if="item.setup_fee_cost != null && item.setup_fee_cost > 0" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0"></span>
                            <span class="text-gray-500">{{ $t("commission_page.setup_fee_cost") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency(item.setup_fee_cost) }}</span>
                          </div>
                          <div v-if="getAddedCosts(item) > 0" class="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="w-2 h-2 rounded-full bg-teal-400 flex-shrink-0"></span>
                            <span class="text-gray-500">{{ $t("commission_page.added_costs") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency(getAddedCosts(item)) }}</span>
                          </div>
                          <div v-if="item.total_cost != null" class="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="text-gray-500">{{ $t("commission_page.total_cost") }}:</span>
                            <span class="font-semibold text-gray-800">{{ formatCurrency((item.total_cost || 0) + getAddedCosts(item)) }}</span>
                          </div>
                        </div>
                      </div>
                      <!-- OMT (Redraw) costs -->
                      <div v-if="item.original_agreement_number && item.redraw_total_cost" class="mt-3 pt-3 border-t border-gray-200">
                        <div class="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <div class="flex items-center gap-1.5 mb-2">
                            <svg class="w-4 h-4 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                            <span class="text-sm font-semibold text-amber-800">{{ $t('note_commission.redraw_costs_title') }}</span>
                          </div>
                          <p class="text-xs text-amber-700 mb-2">{{ $t('note_commission.redraw_costs_description', { original: item.original_agreement_number }) }}</p>
                          <div class="space-y-1">
                            <div v-if="item.redraw_remaining_contract_cost" class="flex justify-between items-center text-xs">
                              <span class="text-gray-700">{{ $t('note_commission.redraw_remaining_contract') }}</span>
                              <span class="font-semibold text-red-600">-{{ formatCurrency(item.redraw_remaining_contract_cost) }}</span>
                            </div>
                            <div v-if="item.redraw_original_commission_paid" class="flex justify-between items-center text-xs">
                              <span class="text-gray-700">{{ $t('note_commission.redraw_original_commission') }}</span>
                              <span class="font-semibold text-red-600">-{{ formatCurrency(item.redraw_original_commission_paid) }}</span>
                            </div>
                            <div class="flex justify-between items-center text-xs pt-1 border-t border-amber-200">
                              <span class="font-semibold text-amber-800">{{ $t('note_commission.redraw_total_deduction') }}</span>
                              <span class="font-bold text-red-700">-{{ formatCurrency(item.redraw_total_cost) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Products breakdown (rejected non-admin) -->
                      <div v-if="item.products && item.products.length" class="mt-3 pt-3 border-t border-red-100">
                        <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.products") }}</div>
                        <div class="border border-gray-200 rounded-lg overflow-hidden">
                          <table class="w-full text-xs">
                            <thead class="bg-gray-50">
                              <tr>
                                <th class="px-3 py-1.5 text-left font-medium text-gray-500">{{ $t("commission_page.product_name") }}</th>
                                <th class="px-3 py-1.5 text-left font-medium text-gray-500">{{ $t("commission_page.type") }}</th>
                                <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.qty") }}</th>
                                <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.sale_price") }}</th>
                                <th class="px-3 py-1.5 text-right font-medium text-gray-500">{{ $t("commission_page.purchase_price") }}</th>
                              </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                              <tr v-for="(prod, pIdx) in item.products" :key="pIdx" class="hover:bg-gray-50">
                                <td class="px-3 py-1.5 text-gray-800">{{ prod.name }}</td>
                                <td class="px-3 py-1.5 text-gray-500">{{ prod.is_cash ? $t('commission_page.cash') : $t('commission_page.rental') }}</td>
                                <td class="px-3 py-1.5 text-right text-gray-700">{{ prod.qty }}</td>
                                <td class="px-3 py-1.5 text-right text-gray-700">{{ formatCurrency(prod.sale_price) }}</td>
                                <td class="px-3 py-1.5 text-right text-gray-700">{{ formatCurrency(prod.purchase_price) }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-if="(item.alarm_center && item.alarm_center.qty > 0) || (item.simcard && item.simcard.qty > 0)" class="mt-2">
                          <div class="text-xs font-medium text-gray-500 uppercase mb-1.5">{{ $t("commission_page.services") }}</div>
                          <div class="flex flex-wrap gap-3">
                            <div v-if="item.alarm_center && item.alarm_center.qty > 0" class="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-xs">
                              <span class="text-blue-600 font-medium">{{ $t("commission_page.alarm_center") }}:</span>
                              <span class="text-gray-800">{{ item.alarm_center.name }}</span>
                              <span class="text-gray-500">{{ item.alarm_center.qty }} × {{ formatCurrency(item.alarm_center.cost_per_month) }}{{ $t("commission_page.per_month") }}</span>
                            </div>
                            <div v-if="item.simcard && item.simcard.qty > 0" class="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded px-3 py-1.5 text-xs">
                              <span class="text-green-600 font-medium">{{ $t("commission_page.simcard") }}:</span>
                              <span class="text-gray-800">{{ item.simcard.name }}</span>
                              <span class="text-gray-500">{{ item.simcard.qty }} × {{ formatCurrency(item.simcard.cost_per_month) }}{{ $t("commission_page.per_month") }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="mt-3 pt-3 border-t border-red-100">
                        <button
                          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          :disabled="fetchingProductsKey === item.key"
                          @click="fetchProductsForCommission(item)"
                        >
                          <svg v-if="fetchingProductsKey === item.key" class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                          <svg v-else class="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                          {{ $t("commission_page.fetch_products") }}
                        </button>
                      </div>
                      <div v-if="hasYearlyBreakdown(item)" class="mt-3">
                        <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.yearly_breakdown") }}</div>
                        <div class="flex flex-wrap gap-2">
                          <div v-for="year in getYearlyBreakdown(item)" :key="year.label" class="bg-white border border-gray-200 rounded px-3 py-1.5 text-sm">
                            <span class="text-gray-500">{{ year.label }}:</span>
                            <span class="ml-1 font-semibold text-gray-600">{{ formatCurrency(year.value) }}</span>
                          </div>
                        </div>
                      </div>
                      <div v-if="item.status_changed_at" class="mt-3 text-xs text-gray-400">
                        {{ $t("commission_page.last_updated") }}: {{ formatDateTime(item.status_changed_at) }}
                        <span v-if="item.status_changed_by"> — {{ item.status_changed_by }}</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
        </div>
    </div>

    <!-- Global Warning Tooltip (fixed position, outside overflow containers) -->
    <div
      v-if="warningTooltip"
      ref="warningTooltipEl"
      class="px-3 py-2 text-xs rounded-lg shadow-lg pointer-events-none"
      :style="warningTooltipStyle"
    >
      <template v-if="warningTooltip.type === 'product'">
        <div v-for="(msg, i) in productWarningText(warningTooltip.item).split(' | ')" :key="i" :style="{ marginTop: i > 0 ? '4px' : '0', color: '#ffffff' }">{{ msg }}</div>
      </template>
      <template v-else>
        <div class="font-semibold mb-1" style="color: #93c5fd;">{{ $t('commission_page.warning_order_mismatch_title') }}</div>
        <div v-for="(msg, i) in orderMismatchMessages(warningTooltip.item)" :key="i" :style="{ marginTop: i > 0 ? '4px' : '0', color: '#ffffff' }">{{ msg }}</div>
      </template>
      <div
        class="absolute w-0 h-0 border-l-4 border-r-4 border-transparent"
        :class="warningTooltip.flipped ? 'border-b-4 bottom-full' : 'border-t-4 top-full'"
        :style="{ left: '8px', [warningTooltip.flipped ? 'borderBottomColor' : 'borderTopColor']: warningTooltip.type === 'product' ? '#111827' : '#1e3a5f' }"
      ></div>
    </div>

    <!-- Change Commission Modal -->
    <modal name="change-commission" height="auto" :scrollable="true" :adaptive="true" width="500px">
      <div class="p-6" v-if="editingCommission">
        <h3 class="text-lg font-semibold mb-1">{{ isAdmin ? $t("commission_page.change_commission") : $t("commission_page.edit_commission") }}</h3>
        <p class="text-sm text-gray-500 mb-4">{{ editingCommission.agreement_number }}</p>

        <div class="space-y-3">
          <div v-if="isAdmin">
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t("commission_page.commission") }}</label>
            <input type="number" v-model.number="editForm.total_commission" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
          <div v-if="isAdmin">
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t("commission_page.hardware_cost") }}</label>
            <input type="number" v-model.number="editForm.hardware_cost" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
          <div v-if="isAdmin">
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t("commission_page.software_cost") }}</label>
            <input type="number" v-model.number="editForm.software_cost" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t("commission_page.extra_monthly") }}</label>
            <input type="number" v-model.number="editForm.extra_monthly_cost" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t("commission_page.extra_cash") }}</label>
            <input type="number" v-model.number="editForm.extra_cash_cost" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
        </div>

        <!-- Live preview -->
        <div class="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
          <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.preview") }}</div>
          <div class="grid grid-cols-3 gap-2 text-center">
            <div>
              <div class="text-xs text-gray-500">{{ $t("commission_page.commission") }}</div>
              <div class="font-semibold text-primary-700">{{ formatCurrency(editForm.total_commission || 0) }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-500">{{ $t("commission_page.hardware_cost") }}</div>
              <div class="font-semibold text-gray-700">{{ formatCurrency(editForm.hardware_cost || 0) }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-500">{{ $t("commission_page.software_cost") }}</div>
              <div class="font-semibold text-gray-700">{{ formatCurrency(editForm.software_cost || 0) }}</div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2 text-center mt-2">
            <div>
              <div class="text-xs text-gray-500">{{ $t("commission_page.extra_monthly") }}</div>
              <div class="font-semibold text-gray-700">{{ formatCurrency(editForm.extra_monthly_cost || 0) }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-500">{{ $t("commission_page.extra_cash") }}</div>
              <div class="font-semibold text-gray-700">{{ formatCurrency(editForm.extra_cash_cost || 0) }}</div>
            </div>
          </div>
          <div class="mt-2 pt-2 border-t border-gray-200 flex justify-between text-sm">
            <span class="text-gray-500">{{ $t("commission_page.total_cost") }}</span>
            <span class="font-semibold text-gray-800">{{ formatCurrency((editForm.total_cost || 0) + (editForm.added_costs || 0)) }}</span>
          </div>
          <!-- Per-year breakdown -->
          <div v-if="editForm.year_2 != null" class="mt-3 pt-3 border-t border-gray-200">
            <div class="text-xs font-medium text-gray-500 uppercase mb-2">{{ $t("commission_page.yearly_breakdown") }}</div>
            <div class="flex flex-wrap gap-2">
              <div v-for="n in 9" :key="n" v-if="editForm['year_' + n] != null" class="bg-white border border-gray-200 rounded px-2 py-1 text-xs">
                <span class="text-gray-500">{{ $t("commission_page.year") }} {{ n }}:</span>
                <span class="ml-1 font-semibold text-primary-700">{{ formatCurrency(editForm['year_' + n]) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-2 mt-6">
          <button @click="$modal.hide('change-commission')" class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
            {{ $t("commission_page.cancel") }}
          </button>
          <button @click="submitChange" class="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded">
            {{ $t("commission_page.save") }}
          </button>
        </div>
      </div>
    </modal>

    <!-- Accept Commission Confirmation Modal -->
    <modal name="accept-commission" height="auto" :scrollable="false" :adaptive="true" width="420px">
      <div class="p-6" v-if="confirmingCommission">
        <div class="flex items-center mb-4">
          <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0">
            <BaseIcon icon="check" class="text-green-600" />
          </div>
          <h3 class="text-lg font-semibold text-gray-800">{{ $t("commission_page.accept") }}</h3>
        </div>
        <p class="text-sm text-gray-600 mb-1">{{ $t("commission_page.confirm_accept") }}</p>
        <p class="text-sm font-medium text-gray-800 mb-4">{{ confirmingCommission.agreement_number }}</p>
        <div class="grid grid-cols-2 gap-3 mb-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t("commission_page.payment_month") }}</label>
            <select v-model.number="acceptMonth" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
              <option v-for="m in 12" :key="m" :value="m">{{ monthName(m) }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t("commission_page.payment_year") }}</label>
            <select v-model.number="acceptYear" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
              <option v-for="y in acceptYearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
        </div>
        <div class="flex justify-end space-x-2">
          <button @click="$modal.hide('accept-commission')" class="px-4 py-2 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
            {{ $t("commission_page.cancel") }}
          </button>
          <button @click="confirmAccept" class="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded">
            {{ $t("commission_page.accept") }}
          </button>
        </div>
      </div>
    </modal>

    <!-- Reject Commission Confirmation Modal -->
    <modal name="reject-commission" height="auto" :scrollable="false" :adaptive="true" width="420px">
      <div class="p-6" v-if="confirmingCommission">
        <div class="flex items-center mb-4">
          <div class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0">
            <BaseIcon icon="times" class="text-red-600" />
          </div>
          <h3 class="text-lg font-semibold text-gray-800">{{ $t("commission_page.reject") }}</h3>
        </div>
        <p class="text-sm text-gray-600 mb-1">{{ $t("commission_page.confirm_reject") }}</p>
        <p class="text-sm font-medium text-gray-800 mb-4">{{ confirmingCommission.agreement_number }}</p>
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t("commission_page.reject_comment") }}</label>
          <textarea
            v-model="rejectComment"
            rows="3"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
            :placeholder="$t('commission_page.reject_comment_placeholder')"
          ></textarea>
        </div>
        <div class="flex justify-end space-x-2">
          <button @click="$modal.hide('reject-commission')" class="px-4 py-2 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
            {{ $t("commission_page.cancel") }}
          </button>
          <button @click="confirmReject" class="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded">
            {{ $t("commission_page.reject") }}
          </button>
        </div>
      </div>
    </modal>

    <!-- Remove Commission Confirm Modal -->
    <modal name="remove-commission" height="auto" :scrollable="false" :adaptive="true" width="420px">
      <div class="p-6" v-if="removingCommission">
        <div class="flex items-center mb-4">
          <div class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0">
            <BaseIcon icon="trash" class="text-red-600" />
          </div>
          <h3 class="text-lg font-semibold text-gray-800">{{ $t("commission_page.remove_commission") }}</h3>
        </div>
        <p class="text-sm text-gray-600 mb-1">{{ $t("commission_page.confirm_remove") }}</p>
        <p class="text-sm font-medium text-gray-800 mb-6">{{ removingCommission.agreement_number }}</p>
        <div class="flex justify-end space-x-2">
          <button @click="$modal.hide('remove-commission')" class="px-4 py-2 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
            {{ $t("commission_page.cancel") }}
          </button>
          <button @click="confirmRemove" class="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded">
            {{ $t("commission_page.remove_commission") }}
          </button>
        </div>
      </div>
    </modal>

    <!-- Debt Balance (Skuldsaldo) Modal -->
    <modal name="debt-balance" height="auto" :scrollable="true" :adaptive="true" width="600px">
      <div class="p-6">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0">
              <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-800">{{ $t("commission_page.outstanding_debt") }}</h3>
              <p class="text-sm text-gray-500">{{ $t("commission_page.debt_description") }}</p>
            </div>
          </div>
          <div class="text-2xl font-bold text-red-600">{{ formatCurrency(totalDebtBalance) }}</div>
        </div>

        <!-- Per-seller breakdown -->
        <div class="mt-4 space-y-3">
          <div v-for="seller in sellersWithDebt" :key="seller._id" class="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <div class="font-medium text-gray-800">{{ seller.name }}</div>
              <div class="flex items-center gap-2">
                <span class="text-lg font-bold text-red-600">{{ formatCurrency(seller.debt_balance) }}</span>
                <button
                  v-if="isAdmin && seller.debt_balance > 0"
                  @click="confirmResetDebt(seller)"
                  class="text-gray-400 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50"
                  :title="$t('commission_page.reset_debt')"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                </button>
              </div>
            </div>
            <!-- Debt history entries -->
            <div v-if="seller.debt_balance_history && seller.debt_balance_history.length > 0" class="mt-2 space-y-1">
              <div v-for="(entry, idx) in seller.debt_balance_history" :key="idx" class="flex items-center justify-between text-xs">
                <div class="flex items-center gap-2">
                  <span v-if="entry.type === 'added'" class="text-red-500">+</span>
                  <span v-else class="text-green-500">&#8635;</span>
                  <span class="text-gray-600">{{ entry.agreement_number || $t("commission_page.debt_reset_label") }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <span :class="entry.type === 'added' ? 'text-red-600 font-medium' : 'text-green-600 font-medium'">
                    {{ entry.type === 'added' ? '+' : '' }}{{ formatCurrency(entry.amount) }}
                  </span>
                  <span class="text-gray-400">{{ formatDate(entry.date) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="text-xs text-gray-400 mt-1">{{ $t("commission_page.no_debt_history") }}</div>
          </div>
        </div>

        <div v-if="sellersWithDebt.length === 0" class="text-center text-gray-400 py-6">
          {{ $t("commission_page.no_outstanding_debt") }}
        </div>

        <div class="flex justify-end mt-6">
          <button @click="$modal.hide('debt-balance')" class="px-4 py-2 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
            {{ $t("commission_page.close") }}
          </button>
        </div>
      </div>
    </modal>

    <!-- Reset All Debt Confirmation Modal -->
    <modal name="reset-all-debt-confirm" height="auto" :scrollable="false" :adaptive="true" width="420px">
      <div class="p-6">
        <div class="flex items-center mb-4">
          <div class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mr-3 flex-shrink-0">
            <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path></svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-800">{{ $t("commission_page.confirm_reset_all_debt") }}</h3>
        </div>
        <p class="text-sm text-gray-600 mb-1">{{ $t("commission_page.confirm_reset_all_debt_text") }}</p>
        <p class="text-sm text-red-600 font-semibold mb-4">{{ formatCurrency(totalDebtBalance) }}</p>
        <div class="flex justify-end space-x-2">
          <button @click="$modal.hide('reset-all-debt-confirm')" class="px-4 py-2 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
            {{ $t("commission_page.cancel") }}
          </button>
          <button @click="executeResetAllDebt" class="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded">
            {{ $t("commission_page.reset_all_debt") }}
          </button>
        </div>
      </div>
    </modal>

    <!-- Reset Single Seller Debt Confirmation Modal -->
    <modal name="reset-debt-confirm" height="auto" :scrollable="false" :adaptive="true" width="420px">
      <div class="p-6" v-if="resetDebtSeller">
        <div class="flex items-center mb-4">
          <div class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mr-3 flex-shrink-0">
            <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path></svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-800">{{ $t("commission_page.confirm_reset_debt") }}</h3>
        </div>
        <p class="text-sm text-gray-600 mb-1">{{ $t("commission_page.confirm_reset_debt_text") }}</p>
        <p class="text-sm font-medium text-gray-800 mb-1">{{ resetDebtSeller.name }}</p>
        <p class="text-sm text-red-600 font-semibold mb-4">{{ formatCurrency(resetDebtSeller.debt_balance) }}</p>
        <div class="flex justify-end space-x-2">
          <button @click="$modal.hide('reset-debt-confirm')" class="px-4 py-2 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
            {{ $t("commission_page.cancel") }}
          </button>
          <button @click="executeResetDebt" class="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded">
            {{ $t("commission_page.reset_debt") }}
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { calculateCostDeduction, calculateCommissionByYear, YEAR_DISTRIBUTIONS, SIMCARD_CATEGORIES } from "@/utils/commission.js";

export default {
  name: "CommissionsView",
  title() {
    return `${this.$t("commission_page.title")} | SecurCloud`;
  },
  data() {
    return {
      user: this.$store.state.user,
      isLoading: true,
      sellers: [],
      ownSellerId: null,
      selectedSellerId: "",
      filterYear: "",
      filterMonth: "",
      expandedKey: null,
      expandedSellerIds: [],
      hoveredNameRow: null,
      warningTooltip: null,
      orderAlarmLogs: [],
      commissionTab: 'pending',
      editingCommission: null,
      confirmingCommission: null,
      rejectComment: "",
      acceptMonth: new Date().getMonth() + 1,
      acceptYear: new Date().getFullYear(),
      editForm: {
        total_commission: 0,
        extra_monthly_cost: 0,
        extra_cash_cost: 0,
      },
      resetDebtSeller: null,
      pollingInterval: null,
      knownCommissionCount: 0,
      fetchingProductsKey: null,
      removingCommission: null,
    };
  },
  computed: {
    isAdmin() {
      return this.user && this.user.role === "admin";
    },
    allCommissions() {
      const items = [];
      for (const seller of this.sellers) {
        if (!seller.commissions || !seller.commissions.length) continue;
        for (const c of seller.commissions) {
          items.push({
            ...c,
            sellerId: seller._id,
            sellerName: seller.name,
            key: seller._id + "_" + (c._id || c.agreement_number),
          });
        }
      }
      return items.sort((a, b) => (a.sellerName || "").localeCompare(b.sellerName || "", undefined, { sensitivity: "base" }));
    },
    activeCommissions() {
      return this.allCommissions.filter(c => c.status !== 'rejected');
    },
    activeNonDebtCommissions() {
      return this.activeCommissions.filter(c => !c.is_debt_closed);
    },
    pendingCommissions() {
      return this.filteredCommissions.filter(c => !c.is_debt_closed && (c.status === 'pending' || c.status === 'changed'));
    },
    acceptedCommissions() {
      return this.filteredCommissions.filter(c => !c.is_debt_closed && c.status === 'accepted');
    },
    cancelledCommissions() {
      return this.filteredCommissions.filter(c => c.is_debt_closed);
    },
    tabbedCommissions() {
      switch (this.commissionTab) {
        case 'accepted': return this.acceptedCommissions;
        case 'cancelled': return this.cancelledCommissions;
        case 'rejected': return this.filteredRejected;
        default: return this.pendingCommissions;
      }
    },
    rejectedCommissions() {
      return this.allCommissions.filter(c => c.status === 'rejected');
    },
    filteredCommissions() {
      let base = this.activeCommissions;
      if (this.selectedSellerId) base = base.filter(c => c.sellerId === this.selectedSellerId);
      if (this.filterYear !== "") {
        base = base.filter(c => {
          const schedule = this.getPaymentSchedule(c);
          if (schedule.length > 0) {
            return schedule.some(p => {
              if (this.filterMonth !== "") return p.year === this.filterYear && p.month === this.filterMonth;
              return p.year === this.filterYear;
            });
          }
          // Fallback for old data without payment schedule
          if (this.filterMonth !== "") return c.accepted_year === this.filterYear && c.accepted_month === this.filterMonth;
          return c.accepted_year === this.filterYear;
        });
      }
      return base;
    },
    filterYearOptions() {
      const years = new Set();
      for (const c of this.activeCommissions) {
        const schedule = this.getPaymentSchedule(c);
        if (schedule.length > 0) {
          for (const p of schedule) years.add(p.year);
        } else if (c.accepted_year) {
          years.add(c.accepted_year);
        }
      }
      return Array.from(years).sort((a, b) => b - a);
    },
    filteredRejected() {
      if (!this.selectedSellerId) return this.rejectedCommissions;
      return this.rejectedCommissions.filter(c => c.sellerId === this.selectedSellerId);
    },
    totalCommission() {
      const base = this.tabbedCommissions;
      if (this.filterYear !== "") {
        let total = 0;
        for (const c of base) {
          const schedule = this.getPaymentSchedule(c);
          if (schedule.length > 0) {
            for (const p of schedule) {
              if (p.year !== this.filterYear) continue;
              if (this.filterMonth !== "" && p.month !== this.filterMonth) continue;
              total += p.amount || 0;
            }
          } else {
            total += c.total_commission || 0;
          }
        }
        return total;
      }
      return base.reduce((sum, c) => sum + (c.total_commission || 0), 0);
    },
    paidCommission() {
      let total = 0;
      for (const c of this.tabbedCommissions) {
        const schedule = this.getPaymentSchedule(c);
        if (schedule.length > 0) {
          for (const p of schedule) {
            if (this.filterYear !== "" && p.year !== this.filterYear) continue;
            if (this.filterYear !== "" && this.filterMonth !== "" && p.month !== this.filterMonth) continue;
            if (p.isPaid) total += p.amount || 0;
          }
        } else if (c.status === 'accepted') {
          total += c.total_commission || 0;
        }
      }
      return total;
    },
    upcomingCommission() {
      let total = 0;
      for (const c of this.tabbedCommissions) {
        const schedule = this.getPaymentSchedule(c);
        if (schedule.length > 0) {
          for (const p of schedule) {
            if (this.filterYear !== "" && p.year !== this.filterYear) continue;
            if (this.filterYear !== "" && this.filterMonth !== "" && p.month !== this.filterMonth) continue;
            if (!p.isPaid) total += p.amount || 0;
          }
        } else if (c.status !== 'accepted') {
          total += c.total_commission || 0;
        }
      }
      return total;
    },
    totalRevenue() {
      return this.tabbedCommissions.reduce((sum, c) => sum + (c.total_revenue || 0), 0);
    },
    avgMargin() {
      const rev = this.totalRevenue;
      if (!rev) return 0;
      const tb = this.tabbedCommissions.reduce((sum, c) => sum + ((c.adjusted_tb != null ? c.adjusted_tb : c.total_tb) || 0), 0);
      return Math.round((tb / rev) * 100 * 10) / 10;
    },
    totalDebtBalance() {
      const list = this.selectedSellerId ? this.sellers.filter(s => s._id === this.selectedSellerId) : this.sellers;
      return list.reduce((sum, s) => sum + (s.debt_balance || 0), 0);
    },
    sellersWithDebt() {
      const list = this.selectedSellerId ? this.sellers.filter(s => s._id === this.selectedSellerId) : this.sellers;
      return list.filter(s => (s.debt_balance || 0) > 0 || (s.debt_balance_history && s.debt_balance_history.length > 0));
    },
    acceptYearOptions() {
      const current = new Date().getFullYear();
      const years = [];
      for (let y = current - 6; y <= current + 3; y++) years.push(y);
      return years;
    },
    topSellersBoard() {
      if (this.commissionTab !== 'accepted' || !this.filterYear) return [];
      const sellerMap = {};
      for (const seller of this.sellers) {
        if (!seller.commissions) continue;
        let totalRevenue = 0;
        let totalCommissionPayout = 0;
        let totalMRR = 0;
        let totalTB = 0;
        let dealCount = 0;
        for (const c of seller.commissions) {
          if (c.status !== 'accepted') continue;
          if (c.is_debt_closed) continue;
          if (c.original_agreement_number) continue;
          const schedule = this.getPaymentSchedule(c);
          if (schedule.length === 0) continue;
          const first = schedule[0];
          if (first.year !== this.filterYear) continue;
          if (this.filterMonth && first.month !== this.filterMonth) continue;
          totalRevenue += c.total_revenue || 0;
          totalCommissionPayout += c.year_1 || 0;
          totalTB += c.adjusted_tb || 0;
          const contractMonths = parseInt(c.contract_period, 10) || 0;
          if (contractMonths > 0) {
            totalMRR += (c.total_revenue || 0) / contractMonths;
          }
          dealCount++;
        }
        if (dealCount > 0) {
          sellerMap[seller._id] = {
            sellerId: seller._id,
            sellerName: seller.name,
            totalRevenue,
            totalCommissionPayout,
            totalMRR,
            totalTB,
            dealCount,
          };
        }
      }
      return Object.values(sellerMap)
        .sort((a, b) => b.totalTB - a.totalTB)
        .slice(0, 3);
    },
    groupedTabbedCommissions() {
      return this.groupBySeller(this.tabbedCommissions);
    },
    groupedRejected() {
      return this.groupBySeller(this.filteredRejected);
    },
    currentGroupedSellers() {
      return this.commissionTab === 'rejected' ? this.groupedRejected : this.groupedTabbedCommissions;
    },
    allSellersExpanded() {
      const groups = this.currentGroupedSellers;
      return groups.length > 0 && groups.every(g => this.expandedSellerIds.includes(g.sellerId));
    },
    ordersByAgreement() {
      const map = {};
      for (const order of this.orderAlarmLogs) {
        if (!order.agreement_number) continue;
        const key = order.agreement_number.trim().toLowerCase();
        // At most one order per agreement number
        map[key] = order;
      }
      return map;
    },
    warningTooltipStyle() {
      if (!this.warningTooltip) return {};
      const t = this.warningTooltip;
      const bg = t.type === 'product' ? '#111827' : '#1e3a5f';
      return {
        position: 'fixed',
        top: t.top != null ? (t.top + 'px') : '-9999px',
        left: (t.left || 0) + 'px',
        zIndex: 99999,
        minWidth: t.type === 'product' ? '180px' : '200px',
        maxWidth: t.type === 'product' ? '260px' : '300px',
        whiteSpace: 'normal',
        backgroundColor: bg,
        color: '#ffffff',
      };
    },
  },
  methods: {
    async fetchCommissions() {
      try {
        this.isLoading = true;
        const commissionsResp = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/sales/commissions`);
        this.sellers = commissionsResp.data.data || [];
        this.ownSellerId = commissionsResp.data.own_seller_id || null;
        this.knownCommissionCount = this.sellers.reduce((sum, s) => sum + (s.commissions ? s.commissions.length : 0), 0);

        // Collect agreement numbers from all commissions, then fetch only matching orders
        const agreementNumbers = [];
        for (const seller of this.sellers) {
          for (const c of (seller.commissions || [])) {
            if (c.agreement_number) agreementNumbers.push(c.agreement_number.trim());
          }
        }
        if (agreementNumbers.length > 0) {
          const ordersResp = await this.axios.get(
            `${process.env.VUE_APP_SERVER_URL}/admin/unit/order-alarm/logs`,
            { params: { agreement_numbers: agreementNumbers.join(',') } }
          ).catch(() => ({ data: { order_alarm_logs: [] } }));
          this.orderAlarmLogs = ordersResp.data.order_alarm_logs || [];
        } else {
          this.orderAlarmLogs = [];
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    toggleAllSellers() {
      if (this.allSellersExpanded) {
        this.expandedSellerIds = [];
      } else {
        this.expandedSellerIds = this.currentGroupedSellers.map(g => g.sellerId);
      }
      this.expandedKey = null;
    },

    toggleExpand(key) {
      this.expandedKey = this.expandedKey === key ? null : key;
    },

    toggleSellerExpand(sellerId) {
      const idx = this.expandedSellerIds.indexOf(sellerId);
      if (idx !== -1) {
        this.expandedSellerIds.splice(idx, 1);
      } else {
        this.expandedSellerIds.push(sellerId);
      }
      this.expandedKey = null;
    },

    showWarningTooltip(evt, item, type) {
      const rect = evt.currentTarget.getBoundingClientRect();
      this.warningTooltip = { type, item, top: null, left: rect.left, flipped: false };
      this.$nextTick(() => {
        const el = this.$refs.warningTooltipEl;
        if (!el) return;
        const h = el.offsetHeight;
        let top = rect.top - h - 8;
        let flipped = false;
        if (top < 4) {
          top = rect.bottom + 8;
          flipped = true;
        }
        this.warningTooltip = { ...this.warningTooltip, top, flipped };
      });
    },

    hideWarningTooltip() {
      this.warningTooltip = null;
    },

    groupBySeller(commissions) {
      const map = {};
      for (const c of commissions) {
        if (!map[c.sellerId]) {
          map[c.sellerId] = { sellerId: c.sellerId, sellerName: c.sellerName, items: [] };
        }
        map[c.sellerId].items.push(c);
      }
      return Object.values(map).map(g => {
        const items = g.items;
        const totalCommission = items.reduce((s, c) => s + (c.total_commission || 0), 0);
        const totalRevenue = items.reduce((s, c) => s + (c.total_revenue || 0), 0);
        const totalCost = items.reduce((s, c) => s + (c.total_cost || 0), 0);
        const totalTB = items.reduce((s, c) => s + ((c.adjusted_tb != null ? c.adjusted_tb : c.total_tb) || 0), 0);
        const withPeriod = items.filter(c => c.contract_period != null);
        const avgContractPeriod = withPeriod.length ? Math.round(withPeriod.reduce((s, c) => s + Number(c.contract_period), 0) / withPeriod.length) : null;
        const avgMargin = totalRevenue > 0 ? Math.round((totalTB / totalRevenue) * 100 * 10) / 10 : null;

        // Compute period-specific payout when filtering accepted tab by year(/month)
        let periodCommission = null;
        if (this.commissionTab === 'accepted' && this.filterYear !== "") {
          let sum = 0;
          for (const c of items) {
            const schedule = this.getPaymentSchedule(c);
            for (const p of schedule) {
              if (p.year !== this.filterYear) continue;
              if (this.filterMonth !== "" && p.month !== this.filterMonth) continue;
              sum += p.amount || 0;
            }
          }
          periodCommission = sum;
        }

        return {
          ...g,
          totalCommission,
          totalRevenue,
          totalCost,
          totalTB,
          avgContractPeriod,
          avgMargin,
          periodCommission,
          count: items.length,
        };
      }).sort((a, b) => (a.sellerName || "").localeCompare(b.sellerName || "", undefined, { sensitivity: "base" }));
    },

    getGroupedCommissionRows(items) {
      const rows = [];
      for (const item of items) {
        rows.push({ type: "item", key: item.key + "_row", item });
        if (this.expandedKey === item.key) {
          rows.push({ type: "detail", key: item.key + "_detail", item });
        }
      }
      return rows;
    },

    getPaymentSchedule(item) {
      if (!item.accepted_month || !item.accepted_year) return [];
      const now = this.moment();
      const payments = [];
      for (let i = 1; i <= 9; i++) {
        const amount = item['year_' + i];
        if (amount == null) continue;
        const payYear = item.accepted_year + (i - 1);
        const payMonth = item.accepted_month;
        const paymentEnd = this.moment({ year: payYear, month: payMonth - 1, day: 1 }).endOf('month');
        const isPaid = item.status === 'accepted' && paymentEnd.isSameOrBefore(now, 'month');
        payments.push({ yearIndex: i, year: payYear, month: payMonth, amount, isPaid });
      }
      return payments;
    },

    getPaymentDisplay(item) {
      const schedule = this.getPaymentSchedule(item);
      if (schedule.length === 0) {
        if (item.accepted_month && item.accepted_year) {
          return this.monthName(item.accepted_month) + ' ' + item.accepted_year;
        }
        return null;
      }
      if (schedule.length === 1) {
        return this.monthName(schedule[0].month) + ' ' + schedule[0].year;
      }
      const first = schedule[0];
      const last = schedule[schedule.length - 1];
      return this.monthName(first.month) + ' ' + first.year + ' – ' + last.year;
    },

    hasYearlyBreakdown(item) {
      return item.year_1 != null || item.year_2 != null;
    },

    getYearlyBreakdown(item) {
      const schedule = this.getPaymentSchedule(item);
      if (schedule.length > 0) {
        return schedule.map(p => ({
          label: `${this.$t("commission_page.year")} ${p.yearIndex}`,
          value: p.amount,
          paymentDate: `${this.monthName(p.month)} ${p.year}`,
          isPaid: p.isPaid
        }));
      }
      // Fallback for old data without accepted_month/year
      const years = [];
      for (let i = 1; i <= 9; i++) {
        const val = item["year_" + i];
        if (val != null) {
          years.push({ label: `${this.$t("commission_page.year")} ${i}`, value: val, paymentDate: null, isPaid: false });
        }
      }
      return years;
    },

    hasProductWarning(item) {
      if (!item.products || !item.products.length) return false;
      const hasZeroCost = item.products.some(p => p.purchase_price === 0 || p.purchase_price == null);
      const hasAlarmWithoutAC = item.products.some(p => p.category === 'alarm') && !item.alarm_center;
      const hasHighMargin = item.margin_percent != null && item.margin_percent > 90;
      const hasDevicesNeedingServices = item.products.some(p => SIMCARD_CATEGORIES.includes(p.category) && !p.is_cash);
      const hasMissingServiceCost = hasDevicesNeedingServices && (
        (item.simcard && (item.simcard.cost_per_month === 0 || item.simcard.cost_per_month == null)) ||
        (item.alarm_center && (item.alarm_center.cost_per_month === 0 || item.alarm_center.cost_per_month == null))
      );
      const hasStaleServiceCost = (item.software_cost === 0 || item.software_cost == null) && (
        (item.simcard && item.simcard.cost_per_month > 0) ||
        (item.alarm_center && item.alarm_center.cost_per_month > 0)
      );
      return hasZeroCost || hasAlarmWithoutAC || hasHighMargin || hasMissingServiceCost || hasStaleServiceCost;
    },

    hasOrderMismatch(item) {
      return this.orderMismatchMessages(item).length > 0;
    },

    orderMismatchMessages(item) {
      if (!item.products || !item.products.length) return [];
      const key = (item.agreement_number || '').trim().toLowerCase();
      const order = this.ordersByAgreement[key];
      if (!order || !order.products || !order.products.length) return [];

      const msgs = [];

      for (const cp of item.products) {
        const commQty = cp.qty || 0;
        const commPrice = cp.sale_price != null ? cp.sale_price : null;

        // 1. Match by variant_id (most precise for flat variant products)
        let op = null;
        let matchedByVariantId = false;
        if (cp.variant_id) {
          op = order.products.find(p =>
            p.variant_id && String(cp.variant_id) === String(p.variant_id)
          );
          if (op) matchedByVariantId = true;
        }

        // 2. Match by product_id === order product _id
        if (!op) {
          op = order.products.find(p =>
            cp.product_id && p._id && String(cp.product_id) === String(p._id)
          );
        }

        // 3. Match by full display_name (commission name = order display_name)
        // This correctly distinguishes flat variant products that share the same type
        if (!op) {
          const cpNameLower = (cp.name || '').toLowerCase();
          if (cpNameLower) {
            op = order.products.find(p => {
              const opDisplay = (p.display_name || '').toLowerCase();
              return opDisplay && cpNameLower === opDisplay;
            });
          }
        }

        // 4. Fallback: match by base name or type (only for non-variant products)
        if (!op) {
          const cpBaseName = (cp.name || '').replace(/ - [^-]+$/, '').toLowerCase();
          const cpType = (cp.type || '').toLowerCase();
          op = order.products.find(p => {
            const opName = (p.name || p.display_name || '').toLowerCase();
            if (cpBaseName && opName && (opName.includes(cpBaseName) || cpBaseName.includes(opName))) return true;
            return cpType && (p.type || '').toLowerCase() === cpType;
          });
        }

        if (!op) {
          // Product exists in commission but is not in the order at all
          if (commQty > 0) {
            msgs.push(this.$t('commission_page.warning_order_product_missing', { product: cp.name }));
          }
          continue;
        }

        let orderQty, orderPrice;

        // Flat variant order product (matched by variant_id or has no nested variants)
        if (matchedByVariantId || !op.variants || !op.variants.length) {
          orderQty = op.qty || 0;
          orderPrice = (op.price != null && op.price !== 0) ? op.price
            : (op.cash_price != null && op.cash_price !== 0) ? op.cash_price
            : null;
        } else {
          // Order product with nested variants — extract variant name from commission name
          const nameParts = (cp.name || '').split(' - ');
          const variantName = nameParts.length > 1 ? nameParts[nameParts.length - 1].toLowerCase() : null;
          // Also try matching by variant_id within nested variants
          let matchedVariant = null;
          if (cp.variant_id) {
            matchedVariant = op.variants.find(v => v._id && String(v._id) === String(cp.variant_id));
          }
          if (!matchedVariant && variantName) {
            matchedVariant = op.variants.find(v => (v.name || '').toLowerCase() === variantName);
          }
          if (!matchedVariant) {
            matchedVariant = op.variants.find(v => (v.qty || 0) > 0);
          }
          const v = matchedVariant || op.variants[0];
          orderQty = v ? (v.qty || 0) : 0;
          orderPrice = v
            ? ((v.price != null && v.price !== 0) ? v.price
              : (v.cash_price != null && v.cash_price !== 0) ? v.cash_price
              : (v.rental_price != null ? v.rental_price : null))
            : null;
        }

        // Skip if this product wasn't included in the order
        if (!orderQty) continue;

        const productLabel = cp.name;

        if (commQty !== orderQty) {
          msgs.push(this.$t('commission_page.warning_order_qty', { product: productLabel, order: orderQty, commission: commQty }));
        }
        if (orderPrice != null && commPrice != null && commPrice !== orderPrice) {
          msgs.push(this.$t('commission_page.warning_order_price', { product: productLabel, order: this.formatCurrency(orderPrice), commission: this.formatCurrency(commPrice) }));
        }
      }

      return msgs;
    },

    productWarningText(item) {
      if (!item.products || !item.products.length) return '';
      const msgs = [];
      if (item.products.some(p => p.purchase_price === 0 || p.purchase_price == null))
        msgs.push(this.$t('commission_page.warning_zero_cost'));
      if (item.products.some(p => p.category === 'alarm') && !item.alarm_center)
        msgs.push(this.$t('commission_page.warning_no_alarm_center'));
      if (item.margin_percent != null && item.margin_percent > 90)
        msgs.push(this.$t('commission_page.warning_high_margin'));
      const hasDevicesNeedingServices = item.products.some(p => SIMCARD_CATEGORIES.includes(p.category) && !p.is_cash);
      if (hasDevicesNeedingServices) {
        if (item.simcard && (item.simcard.cost_per_month === 0 || item.simcard.cost_per_month == null))
          msgs.push(this.$t('commission_page.warning_simcard_zero_cost'));
        if (item.alarm_center && (item.alarm_center.cost_per_month === 0 || item.alarm_center.cost_per_month == null))
          msgs.push(this.$t('commission_page.warning_ac_zero_cost'));
      }
      if ((item.software_cost === 0 || item.software_cost == null) && (
        (item.simcard && item.simcard.cost_per_month > 0) ||
        (item.alarm_center && item.alarm_center.cost_per_month > 0)
      )) {
        msgs.push(this.$t('commission_page.warning_stale_service_cost'));
      }
      return msgs.join(' | ');
    },

    getAddedCosts(item) {
      const extraMonthly = item.extra_monthly_cost || 0;
      const extraCash = item.extra_cash_cost || 0;
      if (!extraMonthly && !extraCash) return 0;
      let contractMonths = parseInt(item.contract_period, 10) || 0;
      if (!contractMonths) {
        let years = 0;
        for (let i = 1; i <= 9; i++) { if (item['year_' + i] != null) years++; }
        contractMonths = (years || 1) * 12;
      }
      return extraCash + (extraMonthly * contractMonths);
    },

    marginClass(percent) {
      if (percent == null) return "text-gray-400";
      if (percent >= 30) return "text-green-600 font-medium";
      if (percent >= 15) return "text-yellow-600 font-medium";
      return "text-red-600 font-medium";
    },

    statusBadgeClass(status) {
      switch (status) {
        case "accepted": return "bg-green-100 text-green-800";
        case "rejected": return "bg-red-100 text-red-800";
        case "changed": return "bg-yellow-100 text-yellow-800";
        default: return "bg-gray-100 text-gray-600";
      }
    },

    statusLabel(status) {
      switch (status) {
        case "accepted": return this.$t("commission_page.status_accepted");
        case "rejected": return this.$t("commission_page.status_rejected");
        case "changed": return this.$t("commission_page.status_changed");
        default: return this.$t("commission_page.status_pending");
      }
    },

    formatCurrency(value) {
      if (value == null) return "—";
      return value.toLocaleString("sv-SE") + " kr";
    },

    monthName(m) {
      return this.moment().month(m - 1).format('MMMM');
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return this.moment(dateStr).format("YYYY-MM-DD");
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "";
      return this.moment(dateStr).format("YYYY-MM-DD HH:mm");
    },

    openChangeModal(item) {
      this.editingCommission = item;
      const yearFields = {};
      for (let i = 1; i <= 9; i++) {
        if (item["year_" + i] != null) yearFields["year_" + i] = item["year_" + i];
      }
      this.editForm = {
        total_commission: item.total_commission || 0,
        hardware_cost: item.hardware_cost || 0,
        software_cost: item.software_cost || 0,
        total_cost: item.total_cost || 0,
        adjusted_tb: item.adjusted_tb || 0,
        extra_monthly_cost: item.extra_monthly_cost || 0,
        extra_cash_cost: item.extra_cash_cost || 0,
        ...yearFields,
      };
      // Always recalculate on modal open so the preview is consistent,
      // regardless of whether the watcher fires (it won't if cost values
      // are the same as the previous modal session).
      this.recalculateCommission();
      this.$modal.show("change-commission");
    },

    async submitChange() {
      try {
        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/sales/commissions/${this.editingCommission.sellerId}`,
          {
            agreement_number: this.editingCommission.agreement_number,
            action: this.isAdmin ? "change" : "seller_edit",
            updated_commission: this.editForm,
          }
        );
        this.$modal.hide("change-commission");
        this.handleSuccess(this.$t("commission_page.change_success"));
        await this.fetchCommissions();
      } catch (error) {
        this.handleError(error);
      }
    },

    acceptCommission(item) {
      this.confirmingCommission = item;
      const now = new Date();
      this.acceptMonth = now.getMonth() + 1;
      this.acceptYear = now.getFullYear();
      this.$modal.show("accept-commission");
    },

    async confirmAccept() {
      try {
        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/sales/commissions/${this.confirmingCommission.sellerId}`,
          {
            agreement_number: this.confirmingCommission.agreement_number,
            action: "accept",
            accepted_month: this.acceptMonth,
            accepted_year: this.acceptYear,
          }
        );
        this.$modal.hide("accept-commission");
        this.handleSuccess(this.$t("commission_page.accept_success"));
        await this.fetchCommissions();
      } catch (error) {
        this.handleError(error);
      }
    },

    rejectCommission(item) {
      this.confirmingCommission = item;
      this.rejectComment = "";
      this.$modal.show("reject-commission");
    },

    removeCommission(item) {
      this.removingCommission = item;
      this.$modal.show("remove-commission");
    },

    async confirmRemove() {
      try {
        await this.axios.delete(
          `${process.env.VUE_APP_SERVER_URL}/sales/commissions/${this.removingCommission.sellerId}/${this.removingCommission._id}`
        );
        this.$modal.hide("remove-commission");
        this.handleSuccess(this.$t("commission_page.remove_success"));
        await this.fetchCommissions();
      } catch (error) {
        this.handleError(error);
      }
    },

    async confirmReject() {
      try {
        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/sales/commissions/${this.confirmingCommission.sellerId}`,
          {
            agreement_number: this.confirmingCommission.agreement_number,
            action: "reject",
            comment: this.rejectComment || undefined,
          }
        );
        this.$modal.hide("reject-commission");
        this.handleError(null, this.$t("commission_page.reject_success"));
        await this.fetchCommissions();
      } catch (error) {
        this.handleError(error);
      }
    },

    recalculateCommission() {
      if (!this.editingCommission) return;
      // Look up seller's custom commission percentage
      const seller = this.sellers.find(s => s._id === this.editingCommission.sellerId);
      const commissionPct = seller && seller.commission_percentage != null ? seller.commission_percentage : 7;
      const commissionRate = commissionPct / 100;
      // Derive contract length from how many year_N fields exist
      let years = 0;
      for (let i = 1; i <= 9; i++) {
        if (this.editingCommission["year_" + i] != null) years++;
      }
      const contractMonths = (years || 1) * 12;

      // Use stored total_tb (TB before any cost deductions) as the base.
      // For older commissions where total_tb was not persisted, reconstruct it
      // from adjusted_tb (or back-calculate via total_commission / rate) plus
      // whatever cost deductions were active when the commission was last saved.
      let baseTB = this.editingCommission.total_tb;
      if (baseTB == null) {
        const storedMonthly = this.editingCommission.extra_monthly_cost || 0;
        const storedCash = this.editingCommission.extra_cash_cost || 0;
        const storedDeduction = calculateCostDeduction(storedMonthly, storedCash, contractMonths);
        const adjustedTB = this.editingCommission.adjusted_tb != null
          ? this.editingCommission.adjusted_tb
          : (this.editingCommission.total_commission || 0) / commissionRate;
        baseTB = adjustedTB + storedDeduction;
      }

      // Adjust baseTB for hardware/software cost changes (delta from original)
      const hwDelta = (this.editForm.hardware_cost || 0) - (this.editingCommission.hardware_cost || 0);
      const swDelta = (this.editForm.software_cost || 0) - (this.editingCommission.software_cost || 0);
      baseTB = baseTB - hwDelta - swDelta;

      const deduction = calculateCostDeduction(
        this.editForm.extra_monthly_cost || 0,
        this.editForm.extra_cash_cost || 0,
        contractMonths
      );
      const newAdjustedTB = Math.max(0, baseTB - deduction);
      // Update total_cost = hardware + software + setup_fee
      this.$set(this.editForm, 'total_cost',
        (this.editForm.hardware_cost || 0) +
        (this.editForm.software_cost || 0) +
        (this.editingCommission.setup_fee_cost || 0)
      );
      this.$set(this.editForm, 'added_costs', Math.round(deduction));
      this.$set(this.editForm, 'total_tb', Math.round(baseTB));
      this.$set(this.editForm, 'adjusted_tb', Math.round(newAdjustedTB));
      const totalRevenue = this.editingCommission.total_revenue || 0;
      this.$set(this.editForm, 'margin_percent', totalRevenue > 0 ? Math.round((newAdjustedTB / totalRevenue) * 100 * 10) / 10 : 0);
      this.editForm._skipNextCommissionWatch = true;
      this.editForm.total_commission = Math.round(newAdjustedTB * commissionRate);
      // Recalculate per-year breakdown
      const { byYear } = calculateCommissionByYear(newAdjustedTB, contractMonths, commissionPct);
      // Clear old year fields first
      for (let i = 1; i <= 9; i++) delete this.editForm["year_" + i];
      byYear.forEach((val, idx) => {
        this.$set(this.editForm, "year_" + (idx + 1), Math.round(val));
      });
    },

    redistributeCommissionByYear() {
      if (!this.editingCommission) return;
      let years = 0;
      for (let i = 1; i <= 9; i++) {
        if (this.editingCommission["year_" + i] != null) years++;
      }
      if (!years) return;
      const total = this.editForm.total_commission || 0;
      const percentages = YEAR_DISTRIBUTIONS[years];
      for (let i = 1; i <= 9; i++) delete this.editForm["year_" + i];
      if (percentages) {
        percentages.forEach((pct, idx) => {
          this.$set(this.editForm, "year_" + (idx + 1), Math.round(total * (pct / 100)));
        });
      } else {
        // Fallback for >6 years: Y1=50%, Y2=25%, Y3=12.5%, rest split equally
        const remaining = total * 0.125 / (years - 3);
        [0.50, 0.25, 0.125].forEach((pct, idx) => {
          this.$set(this.editForm, "year_" + (idx + 1), Math.round(total * pct));
        });
        for (let i = 4; i <= years; i++) {
          this.$set(this.editForm, "year_" + i, Math.round(remaining));
        }
      }
    },

    showDebtBalanceModal() {
      this.$modal.show("debt-balance");
    },

    confirmResetDebt(seller) {
      this.resetDebtSeller = seller;
      this.$modal.show("reset-debt-confirm");
    },

    async executeResetDebt() {
      try {
        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/sales/commissions/${this.resetDebtSeller._id}/reset-debt`
        );
        this.$modal.hide("reset-debt-confirm");
        this.handleSuccess(this.$t("commission_page.debt_reset_success"));
        await this.fetchCommissions();
      } catch (error) {
        this.handleError(error);
      }
    },

    confirmResetAllDebt() {
      if (this.selectedSellerId) {
        const seller = this.sellers.find(s => s._id === this.selectedSellerId);
        if (seller) {
          this.confirmResetDebt(seller);
          return;
        }
      }
      this.$modal.show("reset-all-debt-confirm");
    },

    async executeResetAllDebt() {
      try {
        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/sales/commissions-reset-all-debt`
        );
        this.$modal.hide("reset-all-debt-confirm");
        this.handleSuccess(this.$t("commission_page.debt_reset_all_success"));
        await this.fetchCommissions();
      } catch (error) {
        this.handleError(error);
      }
    },

    async fetchProductsForCommission(item) {
      if (this.fetchingProductsKey) return;
      this.fetchingProductsKey = item.key;

      try {
        // 1. Fetch agreement by agreement_number
        const agResp = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/agreements`,
          { params: { search: item.agreement_number, limit: 20 } }
        );
        const agreements = agResp.data.agreements || agResp.data.data || [];
        const agreement = agreements.find(a => a.agreement_number === item.agreement_number);
        if (!agreement) {
          this.handleError(null, this.$t("commission_page.fetch_products_not_found"));
          return;
        }

        // 2. Fetch product catalog
        const prodResp = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products`);
        const productCatalog = prodResp.data.data || [];

        // 3. Fetch alarm centers
        const acResp = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/alarm-central/`);
        const alarmCenters = acResp.data.data || acResp.data || [];

        const isCashAgreement = agreement.agreement_category === 'cash' || agreement.agreement_category === 'cash_services';

        // 5. Helper functions (replicated from note_commission logic)
        const findProductInCatalog = (ap) => {
          if (ap.product_id) {
            const m = productCatalog.find(p => p._id === ap.product_id);
            if (m) return m;
          }
          if (ap.type) {
            const m = productCatalog.find(p => p.type === ap.type);
            if (m) return m;
          }
          if (ap.name) {
            const m = productCatalog.find(p =>
              p.name === ap.name || (p.name && p.name.toLowerCase() === ap.name.toLowerCase())
            );
            if (m) return m;
          }
          return null;
        };

        const findVariantMatch = (catalogProduct, ap) => {
          if (!catalogProduct.variants || !catalogProduct.variants.length) return null;
          if (ap.variants && ap.variants.length > 0) {
            const av = ap.variants[0];
            return catalogProduct.variants.find(v =>
              v.name === av.name || (av.articleNumber && v.articleNumber === av.articleNumber)
            ) || null;
          }
          if (ap.variant_id || ap.variant_name) {
            return catalogProduct.variants.find(v =>
              String(v._id) === String(ap.variant_id) ||
              v.name === ap.variant_name ||
              (ap.articleNumber && v.articleNumber === ap.articleNumber)
            ) || null;
          }
          return null;
        };

        // 6. Build enriched products
        const hasAnyQty = (p) => {
          if (!p || typeof p !== 'object') return false;
          if ((p.qty || 0) > 0 || (p.quantity || 0) > 0) return true;
          if (p.variants && Array.isArray(p.variants)) {
            return p.variants.some(v => (v.qty || 0) > 0 || (v.quantity || 0) > 0);
          }
          return false;
        };

        const enrichedProducts = [];
        (agreement.products || []).filter(hasAnyQty).forEach(ap => {
          const catalogMatch = findProductInCatalog(ap);
          let category = ap.category || (catalogMatch && catalogMatch.category) || '';

          const isMonthlyOrSoftware = ['addon_monthly', 'other_monthly', 'software', 'simcard'].includes(ap.type) ||
            (catalogMatch && catalogMatch.is_software);
          const isCash = ap.type === 'cash' || ap.type === 'one_time' ||
            ap.type === 'addon_cash' || ap.type === 'other_cash' ||
            (isCashAgreement && !isMonthlyOrSoftware);
          // Products with category 'simcard' (e.g. "Extra simcard", "Extra alarm central") are recurring monthly costs
          const isSoftware = ap.type === 'software' ||
            (catalogMatch && catalogMatch.is_software === true) ||
            Boolean(ap.is_software) ||
            category === 'simcard';

          const activeVariants = (ap.variants || []).filter(v => (v.qty || 0) > 0 || (v.quantity || 0) > 0);
          const parentQty = ap.qty || ap.quantity || 0;

          if (activeVariants.length > 0) {
            activeVariants.forEach(variant => {
              const variantQty = variant.qty || variant.quantity || 0;
              let purchasePrice = 0;
              let displayName = ap.display_name || ap.name || 'Okänd produkt';

              if (variant.purchase_price != null) {
                purchasePrice = variant.purchase_price;
                displayName = catalogMatch
                  ? `${catalogMatch.display_name || catalogMatch.name} - ${variant.name}`
                  : `${displayName} - ${variant.name}`;
              } else if (ap.purchase_price != null && !catalogMatch) {
                purchasePrice = ap.purchase_price;
                displayName = `${displayName} - ${variant.name}`;
              } else if (catalogMatch) {
                const cv = (catalogMatch.variants || []).find(cv2 =>
                  cv2.name === variant.name ||
                  (variant.articleNumber && cv2.articleNumber === variant.articleNumber) ||
                  (variant._id && String(cv2._id) === String(variant._id))
                );
                purchasePrice = cv ? (cv.purchase_price ?? catalogMatch.purchase_price ?? 0) : (catalogMatch.purchase_price || 0);
                displayName = `${catalogMatch.display_name || catalogMatch.name} - ${variant.name}`;
              } else {
                displayName = `${displayName} - ${variant.name}`;
              }

              let salePrice = isCash
                ? (variant.cash_price || variant.price || 0)
                : (variant.rental_price || variant.price || 0);

              enrichedProducts.push({
                product_id: ap.product_id || (catalogMatch && catalogMatch._id) || null,
                variant_id: variant._id || null,
                name: displayName,
                type: ap.type || '',
                category,
                qty: variantQty,
                sale_price: salePrice,
                purchase_price: purchasePrice,
                is_cash: isCash,
                is_software: isSoftware,
              });
            });
          }

          if (parentQty > 0 && activeVariants.length === 0) {
            let purchasePrice = 0;
            let displayName = ap.display_name || ap.name || 'Okänd produkt';
            const isFlatVariant = !!(ap.variant_id || ap.variant_name);

            if (ap.purchase_price != null) {
              purchasePrice = ap.purchase_price;
              // Don't override displayName for flat variant products — they already have the full variant name
              if (catalogMatch && !isFlatVariant) displayName = catalogMatch.display_name || catalogMatch.name || displayName;
            } else if (catalogMatch) {
              let variantMatch = null;
              if (catalogMatch.variants && catalogMatch.variants.length > 0) {
                variantMatch = findVariantMatch(catalogMatch, ap);
              }
              if (variantMatch) {
                purchasePrice = variantMatch.purchase_price ?? catalogMatch.purchase_price ?? 0;
                displayName = `${catalogMatch.display_name || catalogMatch.name} - ${variantMatch.name}`;
              } else {
                purchasePrice = catalogMatch.purchase_price || 0;
                displayName = catalogMatch.display_name || catalogMatch.name || displayName;
              }
            }

            let salePrice = isCash
              ? (ap.cash_price || ap.price || 0)
              : (ap.price || ap.rental_price || 0);

            enrichedProducts.push({
              product_id: ap.product_id || (catalogMatch && catalogMatch._id) || null,
              variant_id: ap.variant_id || null,
              name: displayName,
              type: ap.type || '',
              category,
              qty: parentQty,
              sale_price: salePrice,
              purchase_price: purchasePrice,
              is_cash: isCash,
              is_software: isSoftware,
            });
          }
        });

        // 7. Build alarm center entry (skip for cash agreements — costs are separate products)
        let alarmCenterEntry = undefined;
        if (!isCashAgreement && agreement.alarm_center) {
          const ac = alarmCenters.find(a => a._id === agreement.alarm_center);
          const acName = ac ? ac.name : '';
          if (acName) {
            const acProduct = productCatalog.find(
              p => p.category === 'alarm_central_supplier' && p.name && p.name.trim().toLowerCase() === acName.trim().toLowerCase()
            );
            const acCost = acProduct ? (acProduct.purchase_price || 0) : 0;
            const acQty = enrichedProducts
              .filter(p => p.category === 'alarm')
              .reduce((sum, p) => sum + p.qty, 0);
            if (acCost > 0 && acQty > 0) {
              alarmCenterEntry = {
                product_id: acProduct ? acProduct._id : undefined,
                name: acName,
                cost_per_month: acCost,
                qty: acQty,
              };
            }
          }
        }

        // 8. Build simcard entry (skip for cash agreements — costs are separate products)
        let simcardEntry = undefined;
        if (!isCashAgreement && agreement.sim_card) {
          const simProduct = productCatalog.find(
            p => p.category === 'simcard_supplier' && p.name && p.name.trim().toLowerCase() === agreement.sim_card.trim().toLowerCase()
          );
          const simCost = simProduct ? (simProduct.purchase_price || 0) : 0;
          if (simCost > 0) {
            let simQty = enrichedProducts
              .filter(p => SIMCARD_CATEGORIES.includes(p.category) && !p.is_cash)
              .reduce((sum, p) => sum + p.qty, 0);
            if (simQty > 0) {
              simcardEntry = {
                product_id: simProduct ? simProduct._id : undefined,
                name: agreement.sim_card,
                cost_per_month: simCost,
                qty: simQty,
              };
            }
          }
        }

        // 9. Build additional fields from agreement
        const contractPeriod = agreement.contract_period ? String(agreement.contract_period) : undefined;
        const customerName = agreement.customer_name || undefined;

        // Setup fee cost (995 kr per personlarm / alarm category rental product)
        const personlarmQty = enrichedProducts
          .filter(p => p.category === 'alarm' && !p.is_cash)
          .reduce((sum, p) => sum + p.qty, 0);
        const setupFeeCost = personlarmQty > 0 ? personlarmQty * 995 : undefined;

        // Redraw (OMT) fields
        let originalAgreementNumber, redrawRemainingContractCost, redrawOriginalCommissionPaid, redrawTotalCost;
        if (agreement.original_agreement_number) {
          originalAgreementNumber = agreement.original_agreement_number;
          try {
            const redrawResp = await this.axios.get(
              `${process.env.VUE_APP_SERVER_URL}/agreements/redraw-costs/${agreement.original_agreement_number}`,
              { params: { seller_id: item.sellerId } }
            );
            const orig = redrawResp.data.original;
            const origComm = redrawResp.data.commission;
            if (orig) {
              const periodMatch = (orig.contract_period || '').match(/(\d+)/);
              if (periodMatch && orig.contract_start) {
                const totalMonths = parseInt(periodMatch[1]);
                const contractEnd = this.moment(orig.contract_start).add(totalMonths, 'months');
                const redrawDate = orig.updatedAt ? this.moment(orig.updatedAt) : this.moment();
                const remainingMonths = Math.max(0, Math.ceil(contractEnd.diff(redrawDate, 'months', true)));
                const monthlyCost = parseFloat(orig.rental_month_cost) || 0;
                redrawRemainingContractCost = Math.round(remainingMonths * monthlyCost);
              }
            }
            if (origComm) {
              redrawOriginalCommissionPaid = Math.round(origComm.total_commission || 0);
            }
            if (redrawRemainingContractCost || redrawOriginalCommissionPaid) {
              redrawTotalCost = (redrawRemainingContractCost || 0) + (redrawOriginalCommissionPaid || 0);
            }
          } catch (redrawErr) {
            console.warn('Could not fetch redraw costs:', redrawErr);
          }
        }

        // 10. Save via the backfill_products action (preserves status, targets by _id)
        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/sales/commissions/${item.sellerId}`,
          {
            agreement_number: item.agreement_number,
            commission_id: item._id,
            action: "backfill_products",
            updated_commission: {
              products: enrichedProducts,
              simcard: simcardEntry || undefined,
              alarm_center: alarmCenterEntry || undefined,
              contract_period: contractPeriod,
              customer_name: customerName,
              setup_fee_cost: setupFeeCost,
              original_agreement_number: originalAgreementNumber,
              redraw_remaining_contract_cost: redrawRemainingContractCost,
              redraw_original_commission_paid: redrawOriginalCommissionPaid,
              redraw_total_cost: redrawTotalCost,
            },
          }
        );

        this.handleSuccess(this.$t("commission_page.fetch_products_success"));
        await this.fetchCommissions();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.fetchingProductsKey = null;
      }
    },

    async pollForNewCommissions() {
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/sales/commissions`
        );
        const sellers = response.data.data || [];
        const count = sellers.reduce((sum, s) => sum + (s.commissions ? s.commissions.length : 0), 0);
        if (count !== this.knownCommissionCount) {
          this.sellers = sellers;
          this.ownSellerId = response.data.own_seller_id || null;
          this.knownCommissionCount = count;
        }
      } catch {
        // Silent — polling errors should not disrupt the UI
      }
    },
  },
  watch: {
    "editForm.hardware_cost"() { this.recalculateCommission(); },
    "editForm.software_cost"() { this.recalculateCommission(); },
    "editForm.extra_monthly_cost"() { this.recalculateCommission(); },
    "editForm.extra_cash_cost"() { this.recalculateCommission(); },
    "editForm.total_commission"() {
      if (!this.isAdmin || !this.editingCommission) return;
      if (this.editForm._skipNextCommissionWatch) {
        this.editForm._skipNextCommissionWatch = false;
        return;
      }
      this.redistributeCommissionByYear();
    },
    filterYear(val) { if (!val) this.filterMonth = ""; },
    commissionTab() { this.expandedSellerIds = []; this.expandedKey = null; },
  },
  created() {
    this.setPageTitle(this.$t("commission_page.title"), "sales_commissions");
    this.fetchCommissions();
  },
  mounted() {
    this.pollingInterval = setInterval(this.pollForNewCommissions, 30000);
  },
  beforeDestroy() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
  },
};
</script>

<style scoped>
.commissions-page {
  min-height: calc(100vh - 64px);
  background-color: #f9fafb;
}
</style>
